import React, { useState, useMemo, useEffect, useRef } from 'react';
import { API_URL } from '../config/apiConfig';
import { getUserInfo, getAuthHeader } from '../utils/tokenManager';
import { useParams } from 'react-router-dom';
// default export를 통해 모든 API 함수 가져오기
import apiService from '../services/apiService';
import StockRegistrationModal from './StockRegistrationModal';
const { getSupplyContractPricingTableNo, getSupplyContractPricingInfo, saveHotelPricingTable } = apiService;

/**
 * 통합 가격/재고 캘린더
 * 시즌 관리 + 날짜별 가격/재고 관리를 하나의 캘린더에서 처리
 */
const UnifiedPriceCalendar = ({ 
  roomTypes = [], 
  supplyContractNo: propSupplyContractNo,
  productId: propProductId,
  companyNo: propCompanyNo,
  userId: propUserId,
  onSave,
  // 외부에서 객실 선택 제어를 위한 선택적 props
  selectedRoomType: externalSelectedRoomType,
  selectedRoomProduct: externalSelectedRoomProduct,
  onSelectedRoomTypeChange,
  onSelectedRoomProductChange,
  onRoomListLoaded,
  hideInternalSelectors = false,
}) => {
  // URL 파라미터와 사용자 정보에서 자동 추출
  const { productId: urlProductId } = useParams();
  const userInfo = getUserInfo() || {};
  
  const productId = propProductId || urlProductId;
  const companyNo = propCompanyNo || userInfo.companyNo || '1';
  const userId = propUserId || userInfo.userNo || '152';
  const [supplyContractNo, setSupplyContractNo] = useState(propSupplyContractNo);

  // 시즌 관리
  const [seasonTemplates, setSeasonTemplates] = useState({}); // { 'YYYY-MM-DD': { color, name } }
  // 저장 후 캘린더 가격/재고 데이터 재조회 트리거
  const [priceReloadTick, setPriceReloadTick] = useState(0);
  // 시즌 템플릿 재조회 트리거
  const [seasonReloadTick, setSeasonReloadTick] = useState(0);
  const [seasonTemplatesReady, setSeasonTemplatesReady] = useState(false);
  // 적용된 시즌 인라인 편집 모드 상태
  const [isSeasonEditMode, setIsSeasonEditMode] = useState(false);
  const [editingInlineSeason, setEditingInlineSeason] = useState(null); // { no, name, color }
  
  // 날짜 선택 (드래그 기본)
  const [selectedDates, setSelectedDates] = useState([]); // 필터링된 최종 날짜
  const [rawDates, setRawDates] = useState([]); // 필터링 전 원본 날짜
  
  // 날짜 범위 입력
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  
  // 필터 옵션
  const [weekdayFilters, setWeekdayFilters] = useState([0, 1, 2, 3, 4, 5, 6]); // 기본: 모든 요일
  const [holidayOnly, setHolidayOnly] = useState(false);
  
  // 캘린더 뷰 (기본: 현재 월부터 1년)
  const today = new Date();
  const oneYearLater = new Date(today.getFullYear() + 1, today.getMonth(), 1);
  // 오늘 이전 날짜를 판별하기 위한 기준 (시간 제거)
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  const [calendarStartMonth, setCalendarStartMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  );
  const [calendarEndMonth, setCalendarEndMonth] = useState(
    `${oneYearLater.getFullYear()}-${String(oneYearLater.getMonth() + 1).padStart(2, '0')}`
  );
  
  // 드래그 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [baseSelectedDates, setBaseSelectedDates] = useState([]);
  const [dragMoved, setDragMoved] = useState(false);

  // 모달
  const [isSeasonModalOpen, setIsSeasonModalOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [selectedSeasonPricing, setSelectedSeasonPricing] = useState(null);
  const [editedPricingData, setEditedPricingData] = useState(null);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isSavingPrice, setIsSavingPrice] = useState(false);
  // 판매가 등록 모달 상태
  const [isSalePriceModalOpen, setIsSalePriceModalOpen] = useState(false);
  const [salePriceInput, setSalePriceInput] = useState('');
  const [isSavingSalePrice, setIsSavingSalePrice] = useState(false);
  // 텍스트 일괄 등록 모달 상태
  const [isBulkSaleTextModalOpen, setIsBulkSaleTextModalOpen] = useState(false);
  const [bulkSaleText, setBulkSaleText] = useState('');
  const [isSavingBulkSale, setIsSavingBulkSale] = useState(false);
  // 시즌 편집 모달 상태
  const [isEditSeasonModalOpen, setIsEditSeasonModalOpen] = useState(false);
  const [selectedSeasonNameToEdit, setSelectedSeasonNameToEdit] = useState('');
  const [editingExistingSeason, setEditingExistingSeason] = useState({ name: '', color: '#3B82F6' });
  // 재고 등록 모달 상태
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  // 바깥 클릭으로 선택 초기화 방지 영역 refs
  const calendarAreaRef = useRef(null);
  const selectionActionsRef = useRef(null);
  // 자동 기본 시즌 생성 플래그
  const [autoDefaultSeasonDone, setAutoDefaultSeasonDone] = useState(false);
  const DEFAULT_SEASON_NAME = '시즌 미지정';
  const DEFAULT_SEASON_COLOR = '#FFFFFF';

  // 객실 타입 및 상품 선택
  const [roomTypeList, setRoomTypeList] = useState([]); // 객실타입 리스트
  const [selectedRoomType, setSelectedRoomType] = useState(''); // 선택된 객실타입 (항공패턴)
  const [selectedRoomProduct, setSelectedRoomProduct] = useState(''); // 선택된 객실상품 (요금제)

  // 외부 제어값 우선 사용
  const effectiveSelectedRoomType = externalSelectedRoomType || selectedRoomType;
  const effectiveSelectedRoomProduct = externalSelectedRoomProduct || selectedRoomProduct;
  
  // 날짜별 가격/재고 데이터
  const [priceStockData, setPriceStockData] = useState({}); // { 'YYYY-MM-DD': { 총판매가, 잔여석, ... } }

  // 자원/가격옵션 매핑 (자원번호→자원명, 가격옵션번호→가격옵션명)
  const [resourceOptionMap, setResourceOptionMap] = useState({ roomNameById: {}, productNameById: {} });
  const [resourceOptionList, setResourceOptionList] = useState([]); // 원본 자원/옵션 리스트

  // 인라인 시즌 편집 시작
  const startInlineSeasonEdit = (template) => {
    setEditingInlineSeason({
      no: template?.no ?? null,
      name: template?.name ?? '',
      color: template?.color ?? '#3B82F6'
    });
  };

  // 선택된 날짜들의 공통 값(재고, 마감, 최소숙박일) 계산
  const commonPrefill = useMemo(() => {
    if (!selectedDates || selectedDates.length === 0) {
      return { stock: '', cutoffEnabled: false, cutoffMode: true, minStayEnabled: false, minStayDays: '' };
    }
    // 수집
    const stocks = [];
    const cutoffs = [];
    const minStays = [];
    selectedDates.forEach(d => {
      const row = priceStockData?.[d] || {};
      const stockVal = (row.보유석 != null ? row.보유석 : (row.잔여석 != null ? row.잔여석 : null));
      if (stockVal != null && stockVal !== '') stocks.push(String(stockVal));
      if (typeof row.마감여부 === 'boolean') cutoffs.push(row.마감여부);
      const ms = row.최소투숙일;
      if (ms != null && ms !== '') minStays.push(String(ms));
    });
    // 공통값만 프리필
    const allEqual = (arr) => arr.length > 0 && arr.every(v => v === arr[0]);
    const stock = allEqual(stocks) ? stocks[0] : '';
    const cutoffEqual = allEqual(cutoffs);
    const cutoffEnabled = cutoffEqual && cutoffs.length > 0;
    const cutoffMode = cutoffEnabled ? cutoffs[0] : true;
    const minStayEqual = allEqual(minStays);
    const minStayEnabled = minStayEqual && minStays.length > 0;
    const minStayDays = minStayEnabled ? minStays[0] : '';
    return { stock, cutoffEnabled, cutoffMode, minStayEnabled, minStayDays };
  }, [selectedDates, priceStockData]);

  // 캘린더 영역 밖 클릭 시 선택 초기화 (명시 버튼은 유지)
  useEffect(() => {
    const handleDocMouseDown = (e) => {
      if (!selectedDates || selectedDates.length === 0) return;
      const target = e.target;
      // 모달/오버레이 내부 클릭은 무시
      if (target && typeof target.closest === 'function') {
        if (target.closest('[data-no-clear="true"]')) return;
      }
      const inCalendar = calendarAreaRef.current && calendarAreaRef.current.contains(target);
      const inSelectionActions = selectionActionsRef.current && selectionActionsRef.current.contains(target);
      if (!inCalendar && !inSelectionActions) {
        clearSelection();
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [selectedDates]);

  const cancelInlineSeasonEdit = () => {
    setEditingInlineSeason(null);
  };

  // 인라인 시즌 편집 저장 (시즌번호 우선)
  const saveInlineSeasonEdit = async () => {
    try {
      if (!editingInlineSeason?.name) {
        alert('시즌명을 입력하세요.');
        return;
      }

      // 시즌번호 확보
      let seasonNo = editingInlineSeason.no;
      if (seasonNo == null) {
        const listPayload = {
          Table1: [
            { "컬럼명": "@업체번호", "값": String(companyNo) },
            { "컬럼명": "@상품번호", "값": String(productId) }
          ]
        };
        const listUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
        const listRes = await fetch(listUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(listPayload)
        });
        if (!listRes.ok) throw new Error('시즌 리스트 조회 실패');
        const listData = await listRes.json();
        const seasonInfo = JSON.parse(listData.table?.[0]?.정보 || '[]');
        const target = seasonInfo.find(s => s.시즌명 === editingInlineSeason.name);
        seasonNo = Number(target?.시즌번호 ?? 0);
        if (!seasonNo) throw new Error('시즌번호를 찾을 수 없습니다.');
      }

      const editPayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) },
          { "컬럼명": "@시즌번호", "값": Number(seasonNo) },
          { "컬럼명": "@시즌명", "값": String(editingInlineSeason.name) },
          { "컬럼명": "@시즌색상", "값": String(editingInlineSeason.color) },
          { "컬럼명": "@처리자", "값": String(userId) }
        ]
      };

      const editUrl = `${API_URL}?procedureName=DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%ED%8E%B8%EC%A7%91`;
      const editRes = await fetch(editUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(editPayload)
      });
      if (!editRes.ok) {
        const txt = await editRes.text();
        throw new Error(`시즌 편집 실패: ${editRes.status} - ${txt}`);
      }

      // 화면 데이터 갱신: seasonTemplates 내 해당 시즌(no 또는 name 매칭) 업데이트
      setSeasonTemplates(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(dateStr => {
          const t = next[dateStr] || {};
          const match = (editingInlineSeason.no != null)
            ? t.no === editingInlineSeason.no
            : t.name === editingInlineSeason.name;
          if (match) {
            next[dateStr] = { ...t, name: editingInlineSeason.name, color: editingInlineSeason.color, no: seasonNo };
          }
        });
        return next;
      });

      alert('시즌이 편집되었습니다.');
      setEditingInlineSeason(null);
      // 최신 데이터 반영: 시즌 템플릿 및 가격/재고 재조회
      setSeasonReloadTick(t => t + 1);
      setPriceReloadTick(t => t + 1);
    } catch (err) {
      alert(String(err?.message || err));
    }
  };

  // 객실 타입 및 상품 리스트 로드
  useEffect(() => {
    const loadRoomList = async () => {
      if (!productId || !companyNo) {
        return;
      }

      try {
        const payload = {
          Table1: [
            { "컬럼명": "@업체번호", "값": String(companyNo) },
            { "컬럼명": "@상품번호", "값": String(productId) }
          ]
        };

        

        const url = `${API_URL}?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94%EB%A3%B8%EC%83%81%ED%92%88%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();

          if (data.table && data.table.length > 0 && data.table[0].정보) {
            const roomData = JSON.parse(data.table[0].정보);

            // API 응답 그대로 사용 (객실타입, 객실상품 배열 포함)
            const roomList = roomData
              .filter(room => room.객실상품 && room.객실상품.length > 0) // 객실상품이 있는 것만
              .map(room => ({
                객실타입: room.객실타입,
                객실타입번호: room.객실타입번호,
                객실상품: room.객실상품
              }));

            setRoomTypeList(roomList);

            // 첫 번째 객실타입과 상품 자동 선택 (외부 제어값이 없을 때)
            if (roomList.length > 0 && roomList[0].객실상품.length > 0) {
              const defaultType = roomList[0].객실타입;
              const defaultProduct = roomList[0].객실상품[0].객실상품명;
              if (!externalSelectedRoomType) setSelectedRoomType(defaultType);
              if (!externalSelectedRoomProduct) setSelectedRoomProduct(defaultProduct);
              // 부모 알림
              if (typeof onSelectedRoomTypeChange === 'function' && !externalSelectedRoomType) {
                onSelectedRoomTypeChange(defaultType);
              }
              if (typeof onSelectedRoomProductChange === 'function' && !externalSelectedRoomProduct) {
                onSelectedRoomProductChange(defaultProduct);
              }
            }

            // 부모에 옵션 전달
            if (typeof onRoomListLoaded === 'function') {
              onRoomListLoaded(roomList);
            }
          } else {
          }
        } else {
        }
      } catch (error) {
      }
    };

    loadRoomList();
  }, [productId, companyNo]);

  const ensureSeasonStructures = (seasonIndex) => {
    const updated = { ...editedPricingData };
    if (!updated.시즌) updated.시즌 = [];
    if (!updated.시즌[seasonIndex]) updated.시즌[seasonIndex] = {};
    if (!updated.시즌[seasonIndex].B2C판매가) updated.시즌[seasonIndex].B2C판매가 = { 객실: [] };
    if (!updated.시즌[seasonIndex].B2C판매가.객실) updated.시즌[seasonIndex].B2C판매가.객실 = [];
    if (!updated.시즌[seasonIndex].원가) updated.시즌[seasonIndex].원가 = { 객실: [] };
    if (!updated.시즌[seasonIndex].원가.객실) updated.시즌[seasonIndex].원가.객실 = [];
    return updated;
  };

  const findRoomIndex = (rooms, roomId, roomName) => {
    let idx = -1;
    if (roomId != null) idx = rooms.findIndex(r => r.객실번호 === roomId);
    if (idx === -1) idx = rooms.findIndex(r => r.객실명 === roomName);
    return idx;
  };

  const findProductIndex = (products, productId, productName) => {
    let idx = -1;
    if (productId != null) idx = products.findIndex(p => p.상품번호 === productId);
    if (idx === -1) idx = products.findIndex(p => p.상품명 === productName);
    return idx;
  };

  const handleEditMissingPrice = (roomId, roomName, productId, productName, newValue) => {
    if (!isEditingPrice) return;
    if (!editedPricingData) return;
    const seasonIndex = editedPricingData.시즌?.findIndex(s => (
      selectedSeasonPricing?.template?.no != null
        ? s.시즌번호 === selectedSeasonPricing.template.no
        : s.시즌명 === selectedSeasonPricing.template.name
    )) ?? -1;
    if (seasonIndex === -1) return;

    const updated = ensureSeasonStructures(seasonIndex);

    // B2C판매가 구조 처리
    const bRooms = updated.시즌[seasonIndex].B2C판매가.객실;
    let rIdx = findRoomIndex(bRooms, roomId, roomName);
    if (rIdx === -1) {
      bRooms.push({ 객실번호: roomId, 객실명: roomName, 상품: [] });
      rIdx = bRooms.length - 1;
    }
    const bProducts = bRooms[rIdx].상품 || (bRooms[rIdx].상품 = []);
    let pIdx = findProductIndex(bProducts, productId, productName);
    if (pIdx === -1) {
      bProducts.push({ 상품번호: productId, 상품명: productName, 값: '' });
      pIdx = bProducts.length - 1;
    }
    bProducts[pIdx].값 = newValue;

    // 원가 구조도 동일 인덱스 기준으로 생성/반영
    const cRooms = updated.시즌[seasonIndex].원가.객실;
    let crIdx = findRoomIndex(cRooms, roomId, roomName);
    if (crIdx === -1) {
      cRooms.push({ 객실번호: roomId, 객실명: roomName, 상품: [] });
      crIdx = cRooms.length - 1;
    }
    const cProducts = cRooms[crIdx].상품 || (cRooms[crIdx].상품 = []);
    let cpIdx = findProductIndex(cProducts, productId, productName);
    if (cpIdx === -1) {
      cProducts.push({ 상품번호: productId, 상품명: productName, 값: '' });
      cpIdx = cProducts.length - 1;
    }
    cProducts[cpIdx].값 = newValue;

    setEditedPricingData(updated);
  };

  const getEditedValueFor = (roomId, roomName, productId, productName) => {
    const seasonIndex = editedPricingData?.시즌?.findIndex(s => (
      selectedSeasonPricing?.template?.no != null
        ? s.시즌번호 === selectedSeasonPricing.template.no
        : s.시즌명 === selectedSeasonPricing?.template?.name
    )) ?? -1;
    if (seasonIndex === -1) return '';
    const bRooms = editedPricingData.시즌[seasonIndex]?.B2C판매가?.객실 || [];
    const rIdx = findRoomIndex(bRooms, roomId, roomName);
    if (rIdx === -1) return '';
    const bProducts = bRooms[rIdx]?.상품 || [];
    const pIdx = findProductIndex(bProducts, productId, productName);
    if (pIdx === -1) return '';
    return bProducts[pIdx]?.값 || '';
  };


  // 공급계약번호 자동 로드
  useEffect(() => {
    const loadSupplyContract = async () => {
      if (propSupplyContractNo || !productId) {
        return;
      }
      
      try {
        const payload = {
          Table1: [
            { "컬럼명": "@업체번호", "값": String(companyNo) },
            { "컬럼명": "@상품번호", "값": String(productId) }
          ]
        };

        

        const url = `${API_URL}?procedureName=DBO.TRP_S_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.table && data.table.length > 0 && data.table[0].정보) {
            const contracts = JSON.parse(data.table[0].정보);
            
            if (contracts.length > 0) {
              setSupplyContractNo(contracts[0].공급계약번호);
            } else {
            }
          } else {
          }
        } else {
        }
      } catch (error) {
      }
    };

    loadSupplyContract();
  }, [productId, companyNo, propSupplyContractNo]);

  // 시즌 템플릿 로드
  useEffect(() => {
    const loadSeasonTemplates = async () => {
      if (!productId || !supplyContractNo || !calendarStartMonth || !calendarEndMonth) {
        setSeasonTemplatesReady(false);
        return;
      }

      try {
        // 캘린더 표시 기간에서 시작일/종료일 계산
        const [startYear, startMonth] = calendarStartMonth.split('-').map(Number);
        const [endYear, endMonth] = calendarEndMonth.split('-').map(Number);
        const startDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
        const endDate = new Date(endYear, endMonth, 0); // 해당 월의 마지막 날
        const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

        const payload = {
          Table1: [
            { "컬럼명": "@업체번호", "값": String(companyNo) },
            { "컬럼명": "@상품번호", "값": String(productId) },
            { "컬럼명": "@시작일", "값": startDate },
            { "컬럼명": "@종료일", "값": endDateStr },
            { "컬럼명": "@공급계약번호", "값": Number(supplyContractNo) }
          ]
        };

        

        const url = `${API_URL}?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD_%EC%8B%9C%EC%A6%8C%ED%85%9C%ED%94%8C%EB%A6%BF`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();

          if (data.table && data.table.length > 0 && data.table[0].정보) {
            const seasonData = JSON.parse(data.table[0].정보);

            // 시즌번호 매핑을 위해 시즌 리스트 조회
            let seasonNoByName = {};
            try {
              const listPayload = {
                Table1: [
                  { "컬럼명": "@업체번호", "값": String(companyNo) },
                  { "컬럼명": "@상품번호", "값": String(productId) }
                ]
              };
              const listUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
              const listRes = await fetch(listUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify(listPayload)
              });
              if (listRes.ok) {
                const listData = await listRes.json();
                const arr = JSON.parse(listData?.table?.[0]?.정보 || '[]');
                seasonNoByName = Array.isArray(arr)
                  ? arr.reduce((acc, cur) => { acc[cur.시즌명] = Number(cur.시즌번호); return acc; }, {})
                  : {};
              }
            } catch (e) {
            }

            // 날짜별 시즌 정보를 객체로 변환
            const templates = {};
            seasonData.forEach(item => {
              templates[item.날짜] = {
                color: item.시즌,
                name: item.시즌명,
                no: seasonNoByName[item.시즌명] ?? null
              };
            });

            setSeasonTemplates(templates);
          } else {
            setSeasonTemplates({});
          }
        } else {
        }
      } catch (error) {
      }
      finally {
        setSeasonTemplatesReady(true);
      }
    };

    loadSeasonTemplates();
  }, [productId, companyNo, supplyContractNo, calendarStartMonth, calendarEndMonth, seasonReloadTick]);

  // 날짜별 가격/재고 데이터 로드
  useEffect(() => {
    const loadPriceStockData = async () => {
      if (!productId || !calendarStartMonth || !calendarEndMonth || !effectiveSelectedRoomType || !effectiveSelectedRoomProduct) {
        return;
      }

      try {
        // 캘린더 표시 기간에서 시작일/종료일 계산
        const [startYear, startMonth] = calendarStartMonth.split('-').map(Number);
        const [endYear, endMonth] = calendarEndMonth.split('-').map(Number);
        const startDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
        const endDate = new Date(endYear, endMonth, 0); // 해당 월의 마지막 날
        const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
        // 선택된 객실타입 번호(자원번호) 확인
        const selectedRoom = roomTypeList.find(r => r.객실타입 === effectiveSelectedRoomType);
        const resourceNo = selectedRoom?.객실타입번호;
        if (!resourceNo) {
          return;
        }

        const payload = {
          Table1: [
            { "컬럼명": "@업체번호", "값": String(companyNo) },
            { "컬럼명": "@상품번호", "값": String(productId) },
            { "컬럼명": "@시작일", "값": startDate },
            { "컬럼명": "@종료일", "값": endDateStr },
            { "컬럼명": "@시작시간", "값": "00:00" },
            { "컬럼명": "@자원번호", "값": Number(resourceNo) }
          ]
        };

        

        const url = `${API_URL}?procedureName=DBO.TRP_S_%ED%88%AC%EC%96%B4%EC%9E%AC%EA%B3%A0%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EC%9E%90%EC%9B%90%EA%B8%B0%EA%B0%84_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const data = await response.json();

          const raw = data?.table?.[0]?.재고 ? JSON.parse(data.table[0].재고) : [];
          if (Array.isArray(raw) && raw.length > 0) {
            const priceData = {};
            raw.forEach(item => {
              const dateStr = item.날짜;
              const events = Array.isArray(item.이벤트) ? item.이벤트 : [];
              const getVal = (name) => {
                const f = events.find(e => e.name === name);
                return f?.value ?? null;
              };
              priceData[dateStr] = {
                판매가: getVal('판매가'),
                보유석: getVal('보유석') ?? 0,
                판매석: getVal('판매석') ?? 0,
                잔여석: getVal('잔여석'),
                최소투숙일: getVal('최소투숙일'),
                마감여부: item.마감여부 ?? false
              };
            });

            // 2차: 가격 보강용 RCT 조회 후 병합 (선택된 객실타입/상품만)
            try {
              const rctPayload = {
                Table1: [
                  { "컬럼명": "@상품종류", "값": "호텔", "리스트값": null },
                  { "컬럼명": "@상품번호", "값": String(productId), "리스트값": null },
                  { "컬럼명": "@공급업체번호", "값": String(companyNo), "리스트값": null },
                  { "컬럼명": "@시작일", "값": startDate, "리스트값": null },
                  { "컬럼명": "@종료일", "값": endDateStr, "리스트값": null },
                  { "컬럼명": "@판매여부1", "값": true, "리스트값": null },
                  { "컬럼명": "@판매여부2", "값": true, "리스트값": null },
                  { "컬럼명": "@판매된리스트만보기", "값": false, "리스트값": null },
                  { "컬럼명": "@키워드", "값": "", "리스트값": null },
                  { "컬럼명": "@구분", "값": "3", "리스트값": null },
                  { "컬럼명": "@플랫폼수수료반영", "값": true, "리스트값": null },
                  { "컬럼명": "@결제수수료반영", "값": true, "리스트값": null }
                ]
              };
              const rctUrl = `${API_URL}?procedureName=DBO.RCT_S_%EA%B0%80%EA%B2%A9%EC%98%88%EC%95%BD_02`;
              const rctRes = await fetch(rctUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify(rctPayload)
              });
              if (rctRes.ok) {
                const rctData = await rctRes.json();
                const rows = Array.isArray(rctData?.table) ? rctData.table : [];
                const anyPriceSet = new Set();
                rows.forEach(row => {
                  const dFullAny = row.행사시작일시;
                  const dAny = String(dFullAny || '').slice(0, 10);
                  const anyPrice = Number(onlyDigits(row.판매가));
                  if (!Number.isNaN(anyPrice) && anyPrice > 0) {
                    anyPriceSet.add(dAny);
                  }
                });
                rows.forEach(row => {
                  if (row.항공패턴 === effectiveSelectedRoomType && row.요금제 === effectiveSelectedRoomProduct) {
                    const dFull = row.행사시작일시;
                    const d = String(dFull || '').slice(0, 10); // normalize to YYYY-MM-DD
                    if (!priceData[d]) priceData[d] = {};
                    const np = Number(onlyDigits(row.판매가));
                    priceData[d].판매가 = (!Number.isNaN(np) && np > 0) ? np : priceData[d].판매가;
                    const datePriceNo = (row.가격번호 ?? row.PriceNo ?? row.priceId ?? row.PriceId ?? row.가격ID ?? row.가격식별자);
                    if (datePriceNo != null && datePriceNo !== '' && Number(datePriceNo) !== 0) {
                      priceData[d].가격번호 = Number(datePriceNo);
                    }
                  }
                });
                anyPriceSet.forEach(d => {
                  if (!priceData[d]) priceData[d] = {};
                  priceData[d].anyPricePositive = true;
                });
              } else {
              }
            } catch (e) {
            }

            // 기본 시즌(시즌 미지정) 적용된 날짜에 판매가가 비어있으면 0으로 보강 (단, anyPricePositive 시 보강 금지)
            try {
              Object.keys(priceData).forEach(d => {
                const tmpl = seasonTemplates?.[d];
                if (tmpl && tmpl.name === DEFAULT_SEASON_NAME) {
                  if ((priceData[d].판매가 == null || priceData[d].판매가 === '') && !priceData[d].anyPricePositive) {
                    priceData[d].판매가 = 0;
                  }
                }
              });
            } catch (e) {
              console.warn('기본 시즌 0원 표시 보강 중 경고:', e);
            }

            setPriceStockData(priceData);
} else {
            console.warn('⚠️ 가격/재고 데이터가 없습니다.');
            setPriceStockData({});
          }
        } else {
          console.error('❌ 가격/재고 데이터 조회 실패:', response.status);
        }
      } catch (error) {
        console.error('❌ 가격/재고 데이터 로드 실패:', error);
      }
    };

    loadPriceStockData();
  }, [productId, companyNo, calendarStartMonth, calendarEndMonth, effectiveSelectedRoomType, effectiveSelectedRoomProduct, priceReloadTick, seasonTemplates]);

  // 색상 팔레트
  const colorPalette = [
    { name: '파랑', value: '#3B82F6' },
    { name: '초록', value: '#10B981' },
    { name: '노랑', value: '#F59E0B' },
    { name: '빨강', value: '#EF4444' },
    { name: '보라', value: '#8B5CF6' },
    { name: '분홍', value: '#EC4899' },
  ];

  // 한국 공휴일
  const holidays2025 = [
    '2025-01-01', '2025-01-28', '2025-01-29', '2025-01-30',
    '2025-03-01', '2025-05-05', '2025-05-15', '2025-06-06',
    '2025-08-15', '2025-09-06', '2025-09-07', '2025-09-08',
    '2025-10-03', '2025-10-09', '2025-12-25',
  ];

  const weekdayNames = ['월', '화', '수', '목', '금', '토', '일'];
  const weekdayValues = [1, 2, 3, 4, 5, 6, 0]; // 월=1, 화=2, ..., 일=0

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const onlyDigits = (s) => String(s ?? '').replace(/[^0-9]/g, '');
  const formatKRW = (s) => {
    const v = onlyDigits(s);
    if (!v) return '';
    return v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const isWhiteColor = (c) => {
    if (!c) return false;
    const s = String(c).trim().toLowerCase();
    if (s === '#fff' || s === '#ffffff' || s === 'white') return true;
    if (s.startsWith('rgb')) {
      const m = s.match(/rgba?\(([^)]+)\)/);
      if (m) {
        const parts = m[1].split(',').map(p => parseFloat(p.trim()));
        if (parts[0] === 255 && parts[1] === 255 && parts[2] === 255) {
          if (s.startsWith('rgba')) {
            return parts.length < 4 || parts[3] === 1;
          }
          return true;
        }
      }
    }
    return false;
  };

  // 등록된 시즌 고유 목록 (이름 -> 색상)
  const uniqueSeasons = useMemo(() => {
    const map = {};
    Object.values(seasonTemplates || {}).forEach(t => {
      if (t?.name) {
        if (!(t.name in map)) map[t.name] = t.color || '#3B82F6';
      }
    });
    return map; // { 시즌명: 색상 }
  }, [seasonTemplates]);

  // 다중 월 캘린더 생성
  const calendarMonths = useMemo(() => {
    if (!calendarStartMonth || !calendarEndMonth) return [];
    
    const [startYear, startMonth] = calendarStartMonth.split('-').map(Number);
    const [endYear, endMonth] = calendarEndMonth.split('-').map(Number);
    
    const startDate = new Date(startYear, startMonth - 1, 1);
    const endDate = new Date(endYear, endMonth - 1, 1);
    
    const months = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const monthDate = new Date(currentDate);
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startOffset = firstDay.getDay();
      const dates = [];

      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let j = startOffset - 1; j >= 0; j--) {
        dates.push({ date: new Date(year, month - 1, prevMonthLastDay - j), isCurrentMonth: false });
      }

      for (let j = 1; j <= lastDay.getDate(); j++) {
        dates.push({ date: new Date(year, month, j), isCurrentMonth: true });
      }

      const remainingDays = 42 - dates.length;
      for (let j = 1; j <= remainingDays; j++) {
        dates.push({ date: new Date(year, month + 1, j), isCurrentMonth: false });
      }

      months.push({ year, month, dates });
      
      // 다음 달로 이동
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return months;
  }, [calendarStartMonth, calendarEndMonth]);

  // 표시 범위 내 모든 실제 날짜 문자열('YYYY-MM-DD') 수집
  const visibleDateStrings = useMemo(() => {
    const list = [];
    calendarMonths.forEach(m => {
      m.dates.forEach(({ date, isCurrentMonth }) => {
        if (isCurrentMonth) list.push(formatDate(date));
      });
    });
    return Array.from(new Set(list)).sort();
  }, [calendarMonths]);

  const isDateSelected = (date) => selectedDates.includes(formatDate(date));

  const getSeasonForDate = (date) => {
    const dateStr = formatDate(date);
    const template = seasonTemplates[dateStr];
    if (template) {
      return {
        color: template.color,
        name: template.name
      };
    }
    return null;
  };

  // 기본 시즌 보장: 시즌 생성/적용 및 0원 가격 등록
  useEffect(() => {
    const runAutoDefaultSeason = async () => {
      try {
        if (autoDefaultSeasonDone) return;
        if (!productId || !companyNo || !supplyContractNo) return;
        if (!calendarStartMonth || !calendarEndMonth) return;
        if (!seasonTemplatesReady) return;

        // 시즌 템플릿 로드가 끝난 뒤 동작 (seasonTemplates 사용)
        // 표시기간 중 시즌 미적용 날짜 + "시즌 미지정" 시즌이 적용되었지만 가격번호가 없는 날짜 찾기
        const missingDates = visibleDateStrings.filter(d => {
          const template = seasonTemplates[d];
          // 시즌이 없는 날짜
          if (!template) return true;
          // "시즌 미지정" 시즌이지만 가격번호가 없는 날짜
          if (template.name === DEFAULT_SEASON_NAME) {
            const row = priceStockData?.[d];
            const hasPriceId = row?.가격번호 != null && row.가격번호 !== '' && Number(row.가격번호) !== 0;
            return !hasPriceId;
          }
          return false;
        });
        if (missingDates.length === 0) {
          setAutoDefaultSeasonDone(true);
          return;
        }

        // 1) 시즌 리스트 조회 → '시즌 미지정' 존재 확인, 없으면 생성
        const listPayload = {
          Table1: [
            { "컬럼명": "@업체번호", "값": String(companyNo) },
            { "컬럼명": "@상품번호", "값": String(productId) }
          ]
        };
        const listUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
        let listRes = await fetch(listUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(listPayload)
        });
        if (!listRes.ok) throw new Error('시즌 리스트 조회 실패');
        let listData = await listRes.json();
        let seasonArr = JSON.parse(listData?.table?.[0]?.정보 || '[]');
        let defaultSeason = seasonArr.find(s => s.시즌명 === DEFAULT_SEASON_NAME);

        if (!defaultSeason) {
          // 생성
          const addPayload = {
            Table1: [
              { "컬럼명": "@업체번호", "값": String(companyNo) },
              { "컬럼명": "@상품번호", "값": String(productId) },
              { "컬럼명": "@시즌명", "값": String(DEFAULT_SEASON_NAME) },
              { "컬럼명": "@시즌색상", "값": String(DEFAULT_SEASON_COLOR) },
              { "컬럼명": "@처리자", "값": String(userId) }
            ]
          };
          const addUrl = `${API_URL}?procedureName=DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EC%B6%94%EA%B0%80`;
          const addRes = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(addPayload)
          });
          if (!addRes.ok) throw new Error('기본 시즌 생성 실패');
          // 재조회
          listRes = await fetch(listUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(listPayload)
          });
          if (!listRes.ok) throw new Error('시즌 리스트 재조회 실패');
          listData = await listRes.json();
          seasonArr = JSON.parse(listData?.table?.[0]?.정보 || '[]');
          defaultSeason = seasonArr.find(s => s.시즌명 === DEFAULT_SEASON_NAME);
          if (!defaultSeason) throw new Error('기본 시즌을 찾을 수 없습니다.');
        }

        const seasonNo = Number(defaultSeason.시즌번호);

        // 2) 시즌 템플릿 저장 전에 한 번 더 최신 템플릿 재조회하여 안전 필터링
        let latestTemplates = {};
        try {
          const [startYear, startMonth] = calendarStartMonth.split('-').map(Number);
          const [endYear, endMonth] = calendarEndMonth.split('-').map(Number);
          const startDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
          const endDate = new Date(endYear, endMonth, 0);
          const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
          const rePayload = {
            Table1: [
              { "컬럼명": "@업체번호", "값": String(companyNo) },
              { "컬럼명": "@상품번호", "값": String(productId) },
              { "컬럼명": "@시작일", "값": startDate },
              { "컬럼명": "@종료일", "값": endDateStr },
              { "컬럼명": "@공급계약번호", "값": Number(supplyContractNo) }
            ]
          };
          const reUrl = `${API_URL}?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD_%EC%8B%9C%EC%A6%8C%ED%85%9C%ED%94%8C%EB%A6%BF`;
          const reRes = await fetch(reUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(rePayload)
          });
          if (reRes.ok) {
            const reData = await reRes.json();
            const seasonData = reData?.table && reData.table[0]?.정보 ? JSON.parse(reData.table[0].정보) : [];
            const temp = {};
            seasonData.forEach(item => { temp[item.날짜] = { color: item.시즌, name: item.시즌명 }; });
            latestTemplates = temp;
          }
        } catch (e) {
          // 무시하고 기존 seasonTemplates 사용
          latestTemplates = seasonTemplates || {};
        }

        // "시즌 미지정" 시즌이 적용된 날짜는 latestTemplates에 있어도 처리 대상에 포함
        const safeMissingDates = missingDates.filter(d => {
          const template = latestTemplates[d];
          // 시즌이 없는 날짜는 포함
          if (!template) return true;
          // "시즌 미지정" 시즌이면 포함 (가격번호 생성 필요)
          if (template.name === DEFAULT_SEASON_NAME) return true;
          // 다른 시즌이 적용된 날짜는 제외
          return false;
        });
        const supKey = `noDefaultSeason:${companyNo}:${productId}:${supplyContractNo}`;
        let suppressed = [];
        try { suppressed = JSON.parse(localStorage.getItem(supKey) || '[]'); } catch {}
        const suppressedSet = new Set(Array.isArray(suppressed) ? suppressed : []);
        const zeroOnlyDates = safeMissingDates.filter(d => {
          if (suppressedSet.has(d)) return false;
          const row = priceStockData?.[d];
          if (!row) {
            return true;
          }
          const price = Number(row.판매가 ?? 0);
          const hasPrice = !isNaN(price) && price > 0;
          const leftVal = (row.잔여석 != null && row.잔여석 !== '') ? Number(row.잔여석) :
                          ((row.보유석 != null && row.판매석 != null && row.보유석 !== '' && row.판매석 !== '') ? Number(row.보유석) - Number(row.판매석) : 0);
          const hasLeft = !isNaN(leftVal) && leftVal > 0;
          const anyPositive = !!row.anyPricePositive;
          const hasPriceId = row.가격번호 != null && row.가격번호 !== '' && Number(row.가격번호) !== 0;
          if (anyPositive || hasPriceId) return false;
          return !hasPrice && !hasLeft && leftVal === 0;
        });
        if (zeroOnlyDates.length === 0) {
          setAutoDefaultSeasonDone(true);
          return;
        }

        // 2) 시즌 템플릿 저장: 시즌이 없는 날짜만 기본 시즌에 적용 (이미 시즌 미지정이 적용된 날짜는 제외)
        const datesNeedingTemplate = zeroOnlyDates.filter(d => {
          const template = latestTemplates[d];
          return !template || template.name !== DEFAULT_SEASON_NAME;
        });
        
        if (datesNeedingTemplate.length > 0) {
          const dateList = datesNeedingTemplate.map(d => ({ "날짜": d }));
          const templatePayload = {
            Table1: [
              { "컬럼명": "@업체번호", "값": String(companyNo) },
              { "컬럼명": "@상품번호", "값": String(productId) },
              { "컬럼명": "@날짜리스트", "값": JSON.stringify(dateList) },
              { "컬럼명": "@시즌번호", "값": Number(seasonNo) },
              { "컬럼명": "@공급계약번호", "값": Number(supplyContractNo) },
              { "컬럼명": "@처리자", "값": String(userId) }
            ]
          };
          const templateUrl = `${API_URL}?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD_%EC%8B%9C%EC%A6%8C%ED%85%9C%ED%94%8C%EB%A6%BF%EC%A0%80%EC%9E%A5`;
          const templateRes = await fetch(templateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(templatePayload)
          });
          if (!templateRes.ok) throw new Error('기본 시즌 템플릿 저장 실패');
        }

        // 3) 가격 0원 설정을 위해 자원/가격옵션 리스트 조회
        try {
          const optionPayload = { Table1: [ { "컬럼명": "@상품번호", "값": String(productId) } ] };
          const optionUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8_%EC%9E%90%EC%9B%90%EA%B0%80%EA%B2%A9%EC%98%B5%EC%85%98%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
          const optionRes = await fetch(optionUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(optionPayload)
          });
          if (optionRes.ok) {
            const optionData = await optionRes.json();
            const parsed = optionData?.table && optionData.table[0]?.상품자원요금제 ? JSON.parse(optionData.table[0].상품자원요금제) : [];

            // 4) 요금표 확보
            let pricingTableData = await getSupplyContractPricingTableNo({ contractNo: supplyContractNo });
            if (!pricingTableData || !pricingTableData.요금표번호) {
              const defaultPricingData = { 시즌: [] };
              const defaultSettings = { 테이블데이터: { 시즌: [] }, 선택시즌: [], 시즌목록: [] };
              await saveHotelPricingTable({
                companyNo,
                productNo: productId,
                contractNo: Number(supplyContractNo) || 0,
                pricingTableData: defaultPricingData,
                settingsData: defaultSettings,
                pricingTableNo: 0,
                fileName: '기본 요금표',
                fileType: '1'
              });
              pricingTableData = await getSupplyContractPricingTableNo({ contractNo: supplyContractNo });
            }
            const pricingTableNo = (pricingTableData && pricingTableData.요금표번호) ? pricingTableData.요금표번호 : 0;
            const pricingInfo = await getSupplyContractPricingInfo({ pricingTableNo });
            let pricingData;
            try {
              pricingData = pricingInfo?.요금표 ? JSON.parse(pricingInfo.요금표) : { 시즌: [] };
            } catch {
              pricingData = { 시즌: [] };
            }

            // 시즌 항목 찾기/생성
            let seasonEntry = (pricingData.시즌 || []).find(s => (s.시즌번호 ? Number(s.시즌번호) === seasonNo : s.시즌명 === DEFAULT_SEASON_NAME));
            if (!seasonEntry) {
              seasonEntry = { 시즌번호: seasonNo, 시즌명: DEFAULT_SEASON_NAME, 시즌색: DEFAULT_SEASON_COLOR, 원가: { 객실: [] }, B2C판매가: { 객실: [] }, 채널가격: [], 환산원가: [] };
              if (!Array.isArray(pricingData.시즌)) pricingData.시즌 = [];
              pricingData.시즌.push(seasonEntry);
            }

            // 모든 객실/상품 조합에 대해 0원 설정 (ID 기반)
            const ensureRoomProduct = (sectionKey) => {
              if (!seasonEntry[sectionKey]) seasonEntry[sectionKey] = { 객실: [] };
              if (!Array.isArray(seasonEntry[sectionKey].객실)) seasonEntry[sectionKey].객실 = [];
              parsed.forEach(r => {
                const roomNo = r.자원번호 ?? null;
                const roomName = r.자원명 ?? '';
                let room = seasonEntry[sectionKey].객실.find(x => (roomNo ? x.객실번호 === roomNo : x.객실명 === roomName));
                if (!room) {
                  room = { 객실번호: roomNo, 객실명: roomName, 상품: [] };
                  seasonEntry[sectionKey].객실.push(room);
                }
                (r.요금제리스트 || []).forEach(opt => {
                  const prodNo = opt.가격옵션번호 ?? null;
                  const prodName = opt.가격옵션명 ?? '';
                  let prod = (room.상품 || []).find(p => (prodNo ? p.상품번호 === prodNo : p.상품명 === prodName));
                  if (!room.상품) room.상품 = [];
                  if (!prod) {
                    prod = { 상품번호: prodNo, 상품명: prodName, 값: '0' };
                    room.상품.push(prod);
                  } else {
                    prod.값 = '0';
                  }
                });
              });
            };
            ensureRoomProduct('B2C판매가');
            ensureRoomProduct('원가');

            // 저장
            await saveHotelPricingTable({
              companyNo,
              productNo: productId,
              contractNo: Number(supplyContractNo) || 0,
              pricingTableData: pricingData,
              settingsData: pricingInfo?.설정데이터 || { 테이블데이터: { 시즌: pricingData.시즌 || [] }, 선택시즌: [], 시즌목록: [] },
              pricingTableNo: pricingTableNo || 0,
              fileName: pricingTableNo ? '1' : '기본 요금표',
              fileType: pricingTableNo ? '2' : '1'
            });

            // 5) 실제 가격번호 생성: 날짜별로 0원 가격 등록
            try {
              for (const dateStr of zeroOnlyDates) {
                // 각 객실/상품 조합에 대해 가격번호 생성
                for (const r of parsed) {
                  for (const opt of (r.요금제리스트 || [])) {
                    const pricePayload = {
                      Table1: [
                        { "컬럼명": "@업체번호", "값": String(companyNo) },
                        { "컬럼명": "@상품번호", "값": String(productId) },
                        { "컬럼명": "@자원번호", "값": Number(r.자원번호 || 0) },
                        { "컬럼명": "@가격옵션번호", "값": Number(opt.가격옵션번호 || 0) },
                        { "컬럼명": "@출발일", "값": dateStr },
                        { "컬럼명": "@성인판매가", "값": 0 },
                        { "컬럼명": "@소아판매가", "값": 0 },
                        { "컬럼명": "@유아판매가", "값": 0 },
                        { "컬럼명": "@성인원가", "값": 0 },
                        { "컬럼명": "@소아원가", "값": 0 },
                        { "컬럼명": "@유아원가", "값": 0 },
                        { "컬럼명": "@공급계약번호", "값": Number(supplyContractNo) },
                        { "컬럼명": "@처리자", "값": String(userId) }
                      ]
                    };
                    const priceUrl = `${API_URL}?procedureName=DBO.RCT_T_%ED%98%B8%ED%85%94%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D`;
                    const priceRes = await fetch(priceUrl, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                      body: JSON.stringify(pricePayload)
                    });
                    if (!priceRes.ok) {
                      console.warn(`가격번호 생성 실패 (${dateStr}, 객실:${r.자원번호}, 상품:${opt.가격옵션번호})`);
                    }
                  }
                }
              }
            } catch (priceErr) {
              console.warn('가격번호 생성 중 경고:', priceErr);
            }
          }
        } catch (e) {
          console.warn('기본 시즌 가격 0원 설정 중 경고:', e);
        }

        // UI 갱신 및 플래그
        setSeasonReloadTick(t => t + 1);
        setPriceReloadTick(t => t + 1);
        setAutoDefaultSeasonDone(true);
      } catch (e) {
        console.error('자동 기본 시즌 처리 실패:', e);
        // 재시도 루프 방지를 위해 플래그 설정
        setAutoDefaultSeasonDone(true);
      }
    };

    // 시즌 템플릿 로드 완료 후 실행
    runAutoDefaultSeason();
  }, [autoDefaultSeasonDone, productId, companyNo, supplyContractNo, calendarStartMonth, calendarEndMonth, visibleDateStrings, seasonTemplates, seasonTemplatesReady, priceStockData]);

  const handleDragStart = (date) => {
    setIsDragging(true);
    setDragStart(date);
    setBaseSelectedDates(selectedDates);
    setDragMoved(false);
  };

  const handleDragOver = (date) => {
    if (!isDragging || !dragStart) return;
    const start = dragStart < date ? dragStart : date;
    const end = dragStart < date ? date : dragStart;
    const dates = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(formatDate(d));
    }
    setDragMoved(true);
    // 기존 선택과 현재 드래그 범위를 합집합으로 적용
    const union = Array.from(new Set([...(baseSelectedDates || []), ...dates]));
    applyFilters(union);
    
    // 날짜 범위 필드 동기화
    setRangeStart(formatDate(start));
    setRangeEnd(formatDate(end));
  };

  const handleDragEnd = () => {
    // 드래그 이동이 없었다면 단일 클릭으로 토글 처리
    if (!dragMoved && dragStart) {
      toggleDateSelection(dragStart);
    }
    setIsDragging(false);
    setDragStart(null);
    setBaseSelectedDates([]);
    setDragMoved(false);
  };

  // 시즌 템플릿 저장 직후 자동으로 해당 시즌의 가격 정보를 저장
  const autoSavePricingForSeason = async (template) => {
    try {
      if (!productId || !companyNo || !supplyContractNo) return;
      // 1) 요금표번호 확보 (없으면 기본 생성)
      let pricingTableData = await getSupplyContractPricingTableNo({ contractNo: supplyContractNo });
      if (!pricingTableData || !pricingTableData.요금표번호) {
        const defaultPricingData = { 시즌: [] };
        const defaultSettings = { 테이블데이터: { 시즌: [] }, 선택시즌: [], 시즌목록: [] };
        await saveHotelPricingTable({
          companyNo,
          productNo: productId,
          contractNo: Number(supplyContractNo) || 0,
          pricingTableData: defaultPricingData,
          settingsData: defaultSettings,
          pricingTableNo: 0,
          fileName: '기본 요금표',
          fileType: '1'
        });
        pricingTableData = await getSupplyContractPricingTableNo({ contractNo: supplyContractNo });
      }
      const pricingTableNo = (pricingTableData && pricingTableData.요금표번호) ? pricingTableData.요금표번호 : 0;

      // 2) 기존 요금표 정보 로드
      const pricingInfo = await getSupplyContractPricingInfo({ pricingTableNo });
      let pricingData;
      try {
        pricingData = pricingInfo?.요금표 ? JSON.parse(pricingInfo.요금표) : { 시즌: [] };
      } catch {
        pricingData = { 시즌: [] };
      }

      // 3) 시즌 엔트리 보장 (시즌번호 우선, 없으면 시즌명)
      const seasonExists = (pricingData.시즌 || []).some(s => (
        template?.no != null ? s.시즌번호 === Number(template.no) : s.시즌명 === template?.name
      ));
      if (!seasonExists) {
        if (!Array.isArray(pricingData.시즌)) pricingData.시즌 = [];
        pricingData.시즌.push({
          ...(template?.no != null ? { 시즌번호: Number(template.no) } : {}),
          시즌명: template?.name || '새시즌',
          시즌색: template?.color || '#10B981',
          원가: { 객실: [] },
          B2C판매가: { 객실: [] },
          채널가격: [],
          환산원가: []
        });
      }

      // 4) 설정데이터 확보 후 저장 호출
      const settingsForSave = pricingInfo?.설정데이터 || { 테이블데이터: { 시즌: pricingData.시즌 || [] }, 선택시즌: [], 시즌목록: [] };
      await saveHotelPricingTable({
        companyNo,
        productNo: productId,
        contractNo: Number(supplyContractNo) || 0,
        pricingTableData: pricingData,
        settingsData: settingsForSave,
        pricingTableNo: pricingTableNo || 0,
        fileName: pricingTableNo ? '1' : '기본 요금표',
        fileType: pricingTableNo ? '2' : '1'
      });
      // 가격/재고 데이터 새로고침
      setPriceReloadTick(t => t + 1);
    } catch (e) {
      console.warn('자동 가격 저장 실패:', e);
    }
  };

  const toggleDateSelection = (date) => {
    const dateStr = formatDate(date);
    const newDates = selectedDates.includes(dateStr)
      ? selectedDates.filter(d => d !== dateStr)
      : [...selectedDates, dateStr];
    applyFilters(newDates.sort());
    
    // 날짜 범위 필드 동기화 (선택된 날짜가 있을 때만)
    if (newDates.length > 0) {
      const sortedDates = newDates.sort();
      setRangeStart(sortedDates[0]);
      setRangeEnd(sortedDates[sortedDates.length - 1]);
    }
  };

  // 필터 적용 함수
  const applyFilters = (dates) => {
    // 원본 날짜 저장
    setRawDates(dates);
    
    let filtered = [...dates];
    
    // 요일 필터
    if (weekdayFilters.length < 7) {
      filtered = filtered.filter(dateStr => {
        const d = new Date(dateStr);
        return weekdayFilters.includes(d.getDay());
      });
    }
    
    // 공휴일 필터
    if (holidayOnly) {
      filtered = filtered.filter(dateStr => holidays2025.includes(dateStr));
    }
    
    setSelectedDates(filtered.sort());
  };

  // 날짜 범위 즉시 반영
  const handleRangeStartChange = (value) => {
    setRangeStart(value);
    if (value && rangeEnd && value <= rangeEnd) {
      const dates = [];
      for (let d = new Date(value); d <= new Date(rangeEnd); d.setDate(d.getDate() + 1)) {
        dates.push(formatDate(d));
      }
      applyFilters(dates);
    }
  };

  const handleRangeEndChange = (value) => {
    setRangeEnd(value);
    if (rangeStart && value && rangeStart <= value) {
      const dates = [];
      for (let d = new Date(rangeStart); d <= new Date(value); d.setDate(d.getDate() + 1)) {
        dates.push(formatDate(d));
      }
      applyFilters(dates);
    }
  };

  // 요일 필터 토글
  const toggleWeekdayFilter = (day) => {
    const newFilters = weekdayFilters.includes(day)
      ? weekdayFilters.filter(d => d !== day)
      : [...weekdayFilters, day].sort();
    setWeekdayFilters(newFilters);
    
    // 원본 날짜에서 다시 필터링
    if (rawDates.length > 0) {
      let filtered = [...rawDates];
      
      // 새로운 요일 필터 적용
      if (newFilters.length < 7) {
        filtered = filtered.filter(dateStr => {
          const d = new Date(dateStr);
          return newFilters.includes(d.getDay());
        });
      }
      
      // 공휴일 필터 적용
      if (holidayOnly) {
        filtered = filtered.filter(dateStr => holidays2025.includes(dateStr));
      }
      
      setSelectedDates(filtered.sort());
    }
  };

  // 공휴일 필터 토글
  const toggleHolidayFilter = () => {
    const newHolidayOnly = !holidayOnly;
    setHolidayOnly(newHolidayOnly);
    
    // 공휴일만 선택 시 요일 필터 자동 해제
    if (newHolidayOnly) {
      setWeekdayFilters([0, 1, 2, 3, 4, 5, 6]);
    }
    
    // 원본 날짜에서 다시 필터링
    if (rawDates.length > 0) {
      let filtered = [...rawDates];
      
      // 요일 필터 적용 (공휴일만 선택 시는 적용 안함)
      if (!newHolidayOnly && weekdayFilters.length < 7) {
        filtered = filtered.filter(dateStr => {
          const d = new Date(dateStr);
          return weekdayFilters.includes(d.getDay());
        });
      }
      
      // 새로운 공휴일 필터 적용
      if (newHolidayOnly) {
        filtered = filtered.filter(dateStr => holidays2025.includes(dateStr));
      }
      
      setSelectedDates(filtered.sort());
    }
  };

  // 전체 요일 선택/해제
  const toggleAllWeekdays = () => {
    const newFilters = weekdayFilters.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6];
    setWeekdayFilters(newFilters);
    
    // 원본 날짜에서 다시 필터링
    if (rawDates.length > 0) {
      let filtered = [...rawDates];
      
      // 새로운 요일 필터 적용
      if (newFilters.length < 7) {
        filtered = filtered.filter(dateStr => {
          const d = new Date(dateStr);
          return newFilters.includes(d.getDay());
        });
      }
      
      // 공휴일 필터 적용
      if (holidayOnly) {
        filtered = filtered.filter(dateStr => holidays2025.includes(dateStr));
      }
      
      setSelectedDates(filtered.sort());
    }
  };

  const clearSelection = () => {
    setSelectedDates([]);
    setRawDates([]);
    setRangeStart('');
    setRangeEnd('');
    setWeekdayFilters([0, 1, 2, 3, 4, 5, 6]);
    setHolidayOnly(false);
    setIsDragging(false);
    setDragStart(null);
  };

  // 시즌 편집 열기
  const openEditSeasonModal = () => {
    if (!selectedSeasonNameToEdit) {
      alert('편집할 시즌을 선택하세요.');
      return;
    }
    const currentColor = uniqueSeasons[selectedSeasonNameToEdit] || '#3B82F6';
    setEditingExistingSeason({ name: selectedSeasonNameToEdit, color: currentColor });
    setIsEditSeasonModalOpen(true);
  };

  // 시즌 편집 저장
  const handleSaveEditedSeason = async () => {
    if (!editingExistingSeason?.name) {
      alert('시즌명을 입력하세요.');
      return;
    }
    try {
      // 시즌번호 조회
      const listPayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) }
        ]
      };
      const listUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
      const listRes = await fetch(listUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(listPayload)
      });
      if (!listRes.ok) throw new Error('시즌 리스트 조회 실패');
      const listData = await listRes.json();
      const seasonInfo = JSON.parse(listData.table?.[0]?.정보 || '[]');
      // 선택한 시즌명으로 매칭
      const target = seasonInfo.find(s => s.시즌명 === selectedSeasonNameToEdit);
      if (!target) throw new Error('시즌을 찾을 수 없습니다.');

      const seasonNo = Number(target.시즌번호);

      // 편집 저장 호출
      const editPayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) },
          { "컬럼명": "@시즌번호", "값": seasonNo },
          { "컬럼명": "@시즌명", "값": String(editingExistingSeason.name) },
          { "컬럼명": "@시즌색상", "값": String(editingExistingSeason.color) },
          { "컬럼명": "@처리자", "값": String(userId) }
        ]
      };

      const editUrl = `${API_URL}?procedureName=DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%ED%8E%B8%EC%A7%91`;
      const editRes = await fetch(editUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(editPayload)
      });
      if (!editRes.ok) {
        const txt = await editRes.text();
        throw new Error(`시즌 편집 실패: ${editRes.status} - ${txt}`);
      }

      alert('시즌이 편집되었습니다.');
      setIsEditSeasonModalOpen(false);
      // 최신 데이터 반영: 시즌 템플릿 및 가격/재고 재조회
      setSeasonReloadTick(t => t + 1);
      setPriceReloadTick(t => t + 1);
    } catch (err) {
      console.error('시즌 편집 오류:', err);
      alert(String(err?.message || err));
    }
  };

  const handleCreateSeason = () => {
    if (selectedDates.length === 0) {
      alert('날짜를 먼저 선택해주세요.');
      return;
    }
    setEditingSeason({
      id: null,
      name: '',
      color: colorPalette[0].value,
      selectedDates: [...selectedDates],
      prices: {},
      appliedProducts: [],
    });
    setIsSeasonModalOpen(true);
  };

  // 시즌 가격 정보 조회 함수
  // 가격 편집 함수
  const handleEditPrice = (seasonIndex, roomTypeIndex, productIndex, newValue) => {
    if (!editedPricingData || !isEditingPrice) return;
    
    // 기존 데이터 복사
    const updatedData = { ...editedPricingData };
    
    // 해당 시즌의 가격 정보 업데이트
    if (updatedData.시즌 && updatedData.시즌[seasonIndex]) {
      // B2C판매가 업데이트
      if (updatedData.시즌[seasonIndex].B2C판매가?.객실 && 
          updatedData.시즌[seasonIndex].B2C판매가.객실[roomTypeIndex]?.상품) {
        updatedData.시즌[seasonIndex].B2C판매가.객실[roomTypeIndex].상품[productIndex].값 = newValue;
      }
      
      // 원가 업데이트 (동일하게 유지)
      if (updatedData.시즌[seasonIndex].원가?.객실 && 
          updatedData.시즌[seasonIndex].원가.객실[roomTypeIndex]?.상품) {
        updatedData.시즌[seasonIndex].원가.객실[roomTypeIndex].상품[productIndex].값 = newValue;
      }
    }
    
    // 업데이트된 데이터 저장
    setEditedPricingData(updatedData);
  };
  
  // 시즌 추가 함수
  const handleAddNewSeason = (newSeasonData) => {
    if (!editedPricingData || !isEditingPrice) return;
    
    // 기존 데이터 복사
    const updatedData = { ...editedPricingData };
    
    // 시즌 배열이 없으면 초기화
    if (!updatedData.시즌) {
      updatedData.시즌 = [];
    }
    
    // 신규 시즌 추가
    updatedData.시즌.push(newSeasonData);
    
    // 업데이트된 데이터 저장
    setEditedPricingData(updatedData);
    
    return updatedData;
  };
  
  // 시즌 정보 업데이트 함수
  const updateSettingsData = (settingsData, newSeasonData) => {
    try {
      // 설정 데이터 파싱
      const parsedSettings = typeof settingsData === 'string' ? 
        JSON.parse(settingsData) : settingsData;
      
      // 테이블 데이터에 시즌 정보가 없으면 초기화
      if (!parsedSettings.테이블데이터.시즌) {
        parsedSettings.테이블데이터.시즌 = [];
      }
      
      // 신규 시즌 추가
      parsedSettings.테이블데이터.시즌.push(newSeasonData);
      
      // 선택시즌 및 시즌목록 업데이트
      const newSeasonInfo = {
        시즌번호: newSeasonData.시즌번호,
        시즌명: newSeasonData.시즌명,
        시즌색: newSeasonData.시즌색
      };
      
      if (!parsedSettings.선택시즌) {
        parsedSettings.선택시즌 = [];
      }
      parsedSettings.선택시즌.push(newSeasonInfo);
      
      if (!parsedSettings.시즌목록) {
        parsedSettings.시즌목록 = [];
      }
      parsedSettings.시즌목록.push(newSeasonInfo);
      
      return parsedSettings;
    } catch (error) {
      console.error('설정 데이터 업데이트 실패:', error);
      return settingsData; // 오류 발생 시 기존 데이터 그대로 반환
    }
  };
  
  // 가격 저장 함수
  const handleSavePricing = async () => {
    if (!selectedSeasonPricing || !editedPricingData) {
      alert('저장할 가격 정보가 없습니다.');
      return;
    }
    
    try {
      setIsSavingPrice(true);
      
      const { pricingTableNo, settingsData: originalSettingsData } = selectedSeasonPricing;
      let settingsData = originalSettingsData;
      
      // 신규 시즌 추가 처리
      const originalSeasons = selectedSeasonPricing.pricingData.시즌 || [];
      const editedSeasons = editedPricingData.시즌 || [];
      
      // 신규 시즌 검색 (기존에 없는 시즌명 찾기)
      const newSeasons = editedSeasons.filter(editedSeason => 
        !originalSeasons.some(origSeason => origSeason.시즌명 === editedSeason.시즌명)
      );
      
      // 신규 시즌이 있으면 설정 데이터 업데이트
      if (newSeasons.length > 0) {
        let updatedSettingsData = typeof originalSettingsData === 'string' ? 
          JSON.parse(originalSettingsData) : originalSettingsData;
        
        // 각 신규 시즌에 대해 설정 데이터 업데이트
        for (const newSeason of newSeasons) {
          updatedSettingsData = updateSettingsData(updatedSettingsData, newSeason);
        }
        
        settingsData = updatedSettingsData;
      }
      
      // 저장 전 정규화: 객실/상품 ID 매핑 필수
      const normalizePricingForSave = (data) => {
        const clone = JSON.parse(JSON.stringify(data || {}));
        const unresolved = [];
        const rnById = resourceOptionMap?.roomNameById || {};
        const pnById = resourceOptionMap?.productNameById || {};
        const rIdByName = resourceOptionMap?.roomIdByName || {};
        const pIdByName = resourceOptionMap?.productIdByName || {};
        (clone.시즌 || []).forEach(season => {
          ['원가', 'B2C판매가'].forEach(section => {
            const sec = season?.[section];
            const rooms = sec?.객실;
            if (!Array.isArray(rooms)) return;
            rooms.forEach(room => {
              if (!room.객실번호 && room.객실명 && rIdByName[room.객실명]) {
                room.객실번호 = rIdByName[room.객실명];
              }
              (room.상품 || []).forEach(prod => {
                if (!prod.상품번호 && prod.상품명 && pIdByName[prod.상품명]) {
                  prod.상품번호 = pIdByName[prod.상품명];
                }
                if (!room.객실번호 || !prod.상품번호) {
                  unresolved.push(`${room.객실명 || room.객실번호 || '?'} - ${prod.상품명 || prod.상품번호 || '?'}`);
                }
              });
            });
          });
        });
        return { normalized: clone, unresolved };
      };

      const { normalized: normalizedPricing, unresolved } = normalizePricingForSave(editedPricingData);
      if (unresolved.length > 0) {
        alert(`객실/상품 ID 매핑이 되지 않아 저장할 수 없습니다.\n다음 항목을 확인하세요:\n- ${unresolved.slice(0,5).join('\n- ')}${unresolved.length>5?`\n...외 ${unresolved.length-5}개`:''}`);
        return;
      }

      // 요금표 저장 API 호출 전 데이터 로깅
// saveHotelPricingTable 함수 확인
if (typeof saveHotelPricingTable !== 'function') {
        throw new Error('saveHotelPricingTable 함수가 정의되지 않았습니다.');
      }
      
      // API 호출 시도
      try {
        // 값들이 유효한지 확인
        if (!companyNo) console.warn('🚨 companyNo가 없습니다');
        if (!productId) console.warn('🚨 productId가 없습니다');
        if (!supplyContractNo) console.warn('🚨 supplyContractNo가 없습니다');
        
        // contractNo를 숫자로 변환하여 전달
        const contractNoValue = Number(supplyContractNo) || 0;
// 신규 요금표 여부 판단
        const isNewPricingTable = !pricingTableNo;
        const outFileName = isNewPricingTable ? '기본 요금제' : '1';
        const outFileType = isNewPricingTable ? '1' : '2';
        const outFileNo = isNewPricingTable ? 0 : pricingTableNo;

        // settingsData 메타 정합성 보정
        let fixedSettingsData = settingsData;
        try {
          const obj = typeof settingsData === 'string' ? JSON.parse(settingsData) : (settingsData || {});
          obj.파일구분 = outFileType;
          obj.파일명 = outFileName;
          obj.파일번호 = isNewPricingTable ? '' : String(outFileNo || '');
          // 선택시즌/시즌목록 최소 보강
          if (!obj.선택시즌 || !Array.isArray(obj.선택시즌)) obj.선택시즌 = [];
          if (!obj.시즌목록 || !Array.isArray(obj.시즌목록)) obj.시즌목록 = [];
          fixedSettingsData = obj;
        } catch (e) {
          console.warn('설정데이터 메타 보정 실패(무시 가능):', e);
        }

        await saveHotelPricingTable({
          companyNo,
          productNo: productId,
          contractNo: contractNoValue,
          pricingTableData: normalizedPricing,
          settingsData: fixedSettingsData,
          pricingTableNo: outFileNo,
          fileName: outFileName,
          fileType: outFileType
        });
        
        alert('가격 정보가 성공적으로 저장되었습니다.');
        
        // 현재 보고 있는 데이터 업데이트
        setSelectedSeasonPricing(prev => ({
          ...prev,
          pricingData: normalizedPricing,
          settingsData: fixedSettingsData
        }));
        
        // 편집 모드 종료 및 모달 닫기
        setIsEditingPrice(false);
        setIsPricingModalOpen(false);
        // 캘린더 가격/재고 데이터 새로고침
        setPriceReloadTick((t) => t + 1);
      } catch (apiError) {
        console.error('🔴 API 호출 오류:', apiError);
        throw new Error(`API 호출 오류: ${apiError.message}`);
      }
    } catch (error) {
      console.error('🔴 가격 정보 저장 실패:', error);
      alert(`가격 정보 저장 실패: ${error.message}`);
    } finally {
      setIsSavingPrice(false);
    }
  };
  
  // 시즌 가격 정보 조회 함수
  const handleViewSeasonPricing = async (template) => {
    if (template?.name === DEFAULT_SEASON_NAME) {
      alert('미지정된 시즌은 가격정보를 편집할 수 없습니다. 다른 일반 시즌을 선택하세요.');
      return;
    }
    try {
      // 0단계: 상품의 자원/가격옵션 리스트 조회 (객실/상품명 매핑용)
      let optionListLocal = [];
      if (productId) {
        try {
          const optionPayload = {
            Table1: [
              { "컬럼명": "@상품번호", "값": String(productId) }
            ]
          };
          const optionUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EB%B2%88%ED%98%B8_%EC%9E%90%EC%9B%90%EA%B0%80%EA%B2%A9%EC%98%B5%EC%85%98%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
          const optionRes = await fetch(optionUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
            body: JSON.stringify(optionPayload)
          });
          if (optionRes.ok) {
            const optionData = await optionRes.json();
            if (optionData.table && optionData.table[0]?.상품자원요금제) {
              try {
                const parsed = JSON.parse(optionData.table[0].상품자원요금제);
                optionListLocal = Array.isArray(parsed) ? parsed : [];
                const roomNameById = {};
                const productNameById = {};
                const roomIdByName = {};
                const productIdByName = {};
                parsed.forEach(r => {
                  if (r.자원번호) { roomNameById[r.자원번호] = r.자원명; if (r.자원명) roomIdByName[r.자원명] = r.자원번호; }
                  (r.요금제리스트 || []).forEach(opt => {
                    if (opt.가격옵션번호) { productNameById[opt.가격옵션번호] = opt.가격옵션명; if (opt.가격옵션명) productIdByName[opt.가격옵션명] = opt.가격옵션번호; }
                  });
                });
                setResourceOptionMap({ roomNameById, productNameById, roomIdByName, productIdByName });
                setResourceOptionList(parsed);
              } catch (e) {
                console.warn('자원/가격옵션 JSON 파싱 실패:', e);
              }
            }
          } else {
            console.warn('자원/가격옵션 리스트 조회 실패:', optionRes.status);
          }
        } catch (e) {
          console.warn('자원/가격옵션 리스트 조회 중 오류:', e);
        }
      }

      // 1단계: 요금표번호 획득
      let pricingTableData = await getSupplyContractPricingTableNo({ contractNo: supplyContractNo });
      // 기본 요금표 자동 생성: 요금표가 없을 경우 최소 구조로 생성 후 재조회
      if (!pricingTableData || !pricingTableData.요금표번호) {
        try {
          const defaultPricingData = { 시즌: [] };
          const defaultSettings = { 테이블데이터: { 시즌: [] }, 선택시즌: [], 시즌목록: [] };
          await saveHotelPricingTable({
            companyNo,
            productNo: productId,
            contractNo: Number(supplyContractNo) || 0,
            pricingTableData: defaultPricingData,
            settingsData: defaultSettings,
            pricingTableNo: 0,
            fileName: '기본 요금표',
            fileType: '1'
          });
          // 재조회
          pricingTableData = await getSupplyContractPricingTableNo({ contractNo: supplyContractNo });
        } catch (e) {
          console.error('기본 요금표 생성 실패:', e);
        }
      }
      // pricingTableNo가 여전히 없으면 0으로 처리하여 신규 저장 플로우로 진행
      const pricingTableNo = (pricingTableData && pricingTableData.요금표번호) ? pricingTableData.요금표번호 : 0;
// 2단계: 시즌별 가격 정보 획득
      const pricingInfo = await getSupplyContractPricingInfo({ pricingTableNo });
      // 요금표 정보가 없더라도 모달을 열 수 있도록 기본 구조 생성
      let pricingData;
      let settingsDataLocal;
      if (!pricingInfo || !pricingInfo.요금표) {
        console.warn('⚠️ 요금표 정보 없음: 기본 구조로 초기화하여 편집 모달 오픈');
        // 기본 통화/환율 구조 및 선택 시즌 반영
        const roomId = null;
        const roomName = effectiveSelectedRoomType || '';
        const productIdSel = null;
        const productName = effectiveSelectedRoomProduct || '';
        const seasonTemplate = template || { name: '새시즌', color: '#10B981' };
        pricingData = {
          통화코드: { 원가: 'KRW', 환산원가: [], B2C판매가: 'KRW', 채널가격: [] },
          환율: { 원가: '1', 환산원가: [], B2C판매가: '1', 채널가격: [] },
          환산환율: { 원가: '1', 환산원가: [], B2C판매가: '1.0000', 채널가격: [] },
          시즌: [
            {
              ...(seasonTemplate.no != null ? { 시즌번호: seasonTemplate.no } : {}),
              시즌명: seasonTemplate.name,
              시즌색: seasonTemplate.color || '#10B981',
              원가: { 마크업: '0', 객실: roomName ? [{ 객실번호: roomId, 객실명: roomName, 상품: productName ? [{ 상품번호: productIdSel, 상품명: productName, 값: '0' }] : [] }] : [] },
              환산원가: [],
              B2C판매가: { 마크업: '0', 객실: roomName ? [{ 객실번호: roomId, 객실명: roomName, 상품: productName ? [{ 상품번호: productIdSel, 상품명: productName, 값: '0' }] : [] }] : [] },
              채널가격: []
            }
          ]
        };
        settingsDataLocal = {
          테이블데이터: {
            통화코드: { 원가: 'KRW', 환산원가: [], B2C판매가: 'KRW', 채널가격: [] },
            환율: { 원가: '1', 환산원가: [], B2C판매가: '1', 채널가격: [] },
            환산환율: { 원가: '1', 환산원가: [], B2C판매가: '1.0000', 채널가격: [] },
            시즌: pricingData.시즌
          },
          파일구분: '1',
          파일명: '기본 요금제',
          파일번호: '',
          선택통화: [],
          통화목록: [
            { 통화코드: 'USD', 통화명: '미국 달러' },
            { 통화코드: 'KRW', 통화명: '원화' },
            { 통화코드: 'VND', 통화명: '베트남 동' },
            { 통화코드: 'CNY', 통화명: '중국 위안' },
            { 통화코드: 'JPY', 통화명: '일본 엔화' },
            { 통화코드: 'EUR', 통화명: '유로' },
            { 통화코드: 'THB', 통화명: '태국 바트' }
          ],
          선택요금제: productName ? [{ 가격옵션번호: null }] : [],
          상품자원요금제: roomName && productName ? [
            { 자원번호: null, 자원명: roomName, 요금제리스트: [{ 가격옵션번호: null, 가격옵션명: productName, 합산가격여부: 'Y' }] }
          ] : [],
          선택티어: [],
          티어목록: [],
          선택시즌: [{ ...(seasonTemplate.no != null ? { 시즌번호: seasonTemplate.no } : {}), 시즌명: seasonTemplate.name, 시즌색: seasonTemplate.color || '#10B981' }],
          시즌목록: [{ ...(seasonTemplate.no != null ? { 시즌번호: seasonTemplate.no } : {}), 시즌명: seasonTemplate.name, 시즌색: seasonTemplate.color || '#10B981' }]
        };
      } else {
        // 요금표 JSON 파싱
        try {
          pricingData = JSON.parse(pricingInfo.요금표);
} catch (e) {
          console.error('요금표 JSON 파싱 실패:', e);
          alert('요금표 데이터 형식이 올바르지 않습니다.');
          return;
        }
      }
      
      // 선택한 시즌 정보 필터링 (없으면 빈 시즌 생성)
      let seasonInfo = pricingData.시즌?.find(season => (
        template?.no != null ? season.시즌번호 === template.no : season.시즌명 === template.name
      ));
      if (!seasonInfo) {
        if (!Array.isArray(pricingData.시즌)) pricingData.시즌 = [];
        const newSeason = {
          ...(template?.no != null ? { 시즌번호: template.no } : {}),
          시즌명: template.name,
          B2C판매가: { 객실: [] },
          원가: { 객실: [] }
        };
        pricingData.시즌.push(newSeason);
        seasonInfo = newSeason;
      }
      
      // 선택한 객실타입과 객실상품에 해당하는 가격 정보 필터링
      const roomTypeInfo = effectiveSelectedRoomType ? 
        seasonInfo.B2C판매가?.객실?.find(room => room.객실명 === effectiveSelectedRoomType) : null;
      
      const productInfo = roomTypeInfo && effectiveSelectedRoomProduct ? 
        roomTypeInfo.상품?.find(product => product.상품명 === effectiveSelectedRoomProduct) : null;
      
      // 기본 시즌(시즌 미지정)인 경우: 모든 객실/상품에 0원 강제 등록 후 저장
      if (template?.name === DEFAULT_SEASON_NAME) {
        try {
          // 시즌 엔트리 보장
          const ensureSection = (sectionKey) => {
            if (!seasonInfo[sectionKey]) seasonInfo[sectionKey] = { 객실: [] };
            if (!Array.isArray(seasonInfo[sectionKey].객실)) seasonInfo[sectionKey].객실 = [];
            (optionListLocal.length ? optionListLocal : (resourceOptionList || [])).forEach(r => {
              const roomNo = r.자원번호 ?? null;
              const roomName = r.자원명 ?? '';
              let room = seasonInfo[sectionKey].객실.find(x => (roomNo ? x.객실번호 === roomNo : x.객실명 === roomName));
              if (!room) {
                room = { 객실번호: roomNo, 객실명: roomName, 상품: [] };
                seasonInfo[sectionKey].객실.push(room);
              }
              (r.요금제리스트 || []).forEach(opt => {
                const prodNo = opt.가격옵션번호 ?? null;
                const prodName = opt.가격옵션명 ?? '';
                let prod = (room.상품 || []).find(p => (prodNo ? p.상품번호 === prodNo : p.상품명 === prodName));
                if (!room.상품) room.상품 = [];
                if (!prod) {
                  prod = { 상품번호: prodNo, 상품명: prodName, 값: '0' };
                  room.상품.push(prod);
                } else {
                  // 값이 비어있거나 미등록이면 0으로 설정
                  if (prod.값 == null || prod.값 === '') prod.값 = '0';
                }
              });
            });
          };
          ensureSection('B2C판매가');
          ensureSection('원가');

          // 저장 전 settings 데이터 확보
          const settingsForSave = pricingInfo?.설정데이터 || settingsDataLocal || { 테이블데이터: { 시즌: pricingData.시즌 || [] }, 선택시즌: [], 시즌목록: [] };
          await saveHotelPricingTable({
            companyNo,
            productNo: productId,
            contractNo: Number(supplyContractNo) || 0,
            pricingTableData: pricingData,
            settingsData: settingsForSave,
            pricingTableNo: pricingTableNo || 0,
            fileName: pricingTableNo ? '1' : '기본 요금표',
            fileType: pricingTableNo ? '2' : '1'
          });
          // 새로고침 트리거
          setPriceReloadTick(t => t + 1);
        } catch (e) {
          console.warn('기본 시즌 0원 강제 등록 중 경고:', e);
        }
      }

      // 모달에 표시할 데이터 설정
      setSelectedSeasonPricing({
        seasonInfo,
        pricingData,
        roomTypeInfo,
        productInfo,
        template,
        pricingTableNo,
        settingsData: pricingInfo?.설정데이터 || settingsDataLocal || null
      });
      
      // 편집용 데이터 초기화 (기존 데이터를 복사하여 사용)
      setEditedPricingData(JSON.parse(JSON.stringify(pricingData)));
      // 신규 시즌이거나 데이터가 비어있으면 바로 편집 모드
      setIsEditingPrice(true);
      
      // 가격 정보 모달 열기
      setIsPricingModalOpen(true);
      
    } catch (error) {
      console.error('시즌 가격 정보 조회 실패:', error);
      alert(`시즌 가격 정보 조회 실패: ${error.message}`);
    }
  };

  // 테이블 렌더링용 행 구성: 기존 행 + 미등록(누락) 행
  const getPricingRows = () => {
    if (!selectedSeasonPricing) return [];
    const data = (isEditingPrice ? editedPricingData : selectedSeasonPricing.pricingData);
    const season = data?.시즌?.find(s => (
      selectedSeasonPricing?.template?.no != null
        ? s.시즌번호 === selectedSeasonPricing.template.no
        : s.시즌명 === selectedSeasonPricing.template.name
    ));
    const existing = [];
    const keyOf = (room, product) => {
      const rid = room?.객실번호 ?? null;
      const pid = product?.상품번호 ?? null;
      const idKey = (rid && pid) ? `id:${rid}|${pid}` : null;
      const nameKey = `name:${room?.객실명}|${product?.상품명}`;
      return { idKey, nameKey };
    };
    season?.B2C판매가?.객실?.forEach((room, rIdx) => {
      (room.상품 || []).forEach((product, pIdx) => {
        const keys = keyOf(room, product);
        existing.push({ type: 'existing', room, product, rIdx, pIdx, key: keys.idKey || keys.nameKey, idKey: keys.idKey, nameKey: keys.nameKey });
      });
    });
    const existingKeys = new Set();
    existing.forEach(e => { if (e.idKey) existingKeys.add(e.idKey); if (e.nameKey) existingKeys.add(e.nameKey); });
    const missing = [];
    (resourceOptionList || []).forEach(r => {
      (r.요금제리스트 || []).forEach(opt => {
        const idKey = `id:${r.자원번호}|${opt.가격옵션번호}`;
        const nameKey = `name:${resourceOptionMap.roomNameById[r.자원번호] || r.자원명}|${resourceOptionMap.productNameById[opt.가격옵션번호] || opt.가격옵션명}`;
        if (!existingKeys.has(idKey) && !existingKeys.has(nameKey)) {
          missing.push({
            type: 'missing',
            room: { 객실번호: r.자원번호, 객실명: resourceOptionMap.roomNameById[r.자원번호] || r.자원명 },
            product: { 상품번호: opt.가격옵션번호, 상품명: resourceOptionMap.productNameById[opt.가격옵션번호] || opt.가격옵션명 },
            key: idKey
          });
        }
      });
    });
    return [...existing, ...missing];
  };

  const handleUpdateSeasonTemplate = async (template) => {
    if (!confirm(`"${template.name}" 시즌에 선택한 ${selectedDates.length}개 날짜를 적용하시겠습니까?`)) {
      return;
    }

    try {
      // 시즌번호 우선 사용, 없으면 조회하여 획득
      let seasonNo = template?.no != null ? Number(template.no) : null;
      if (seasonNo == null) {
        const listPayload = {
          Table1: [
            { "컬럼명": "@업체번호", "값": String(companyNo) },
            { "컬럼명": "@상품번호", "값": String(productId) }
          ]
        };
        const listUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
        const listResponse = await fetch(listUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(listPayload)
        });
        if (!listResponse.ok) throw new Error('시즌 리스트 조회 실패');
        const listData = await listResponse.json();
        const seasonInfo = JSON.parse(listData.table?.[0]?.정보 || '[]');
        const targetSeason = seasonInfo.find(s => s.시즌명 === template.name);
        if (!targetSeason) throw new Error('시즌을 찾을 수 없습니다.');
        seasonNo = Number(targetSeason.시즌번호);
      }

      const dateList = selectedDates.map(date => ({ "날짜": date }));
      const templatePayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) },
          { "컬럼명": "@날짜리스트", "값": JSON.stringify(dateList) },
          { "컬럼명": "@시즌번호", "값": Number(seasonNo) },
          { "컬럼명": "@공급계약번호", "값": Number(supplyContractNo) },
          { "컬럼명": "@처리자", "값": String(userId) }
        ]
      };

      const templateUrl = `${API_URL}?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD_%EC%8B%9C%EC%A6%8C%ED%85%9C%ED%94%8C%EB%A6%BF%EC%A0%80%EC%9E%A5`;
      const templateResponse = await fetch(templateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(templatePayload)
      });

      if (!templateResponse.ok) {
        const errText = await templateResponse.text();
        throw new Error(`시즌 템플릿 저장 실패: ${templateResponse.status} - ${errText}`);
      }

      alert(`"${template.name}" 시즌이 업데이트되었습니다.`);
      clearSelection();
      // 시즌 템플릿 재로드 (전체 새로고침 대신 상태 트리거)
      setSeasonReloadTick(t => t + 1);
      setPriceReloadTick(t => t + 1);

      // 시즌 템플릿 저장 직후 자동 가격 저장 수행
      await autoSavePricingForSeason({ no: seasonNo, name: template.name, color: template.color });

    } catch (error) {
      console.error('시즌 업데이트 오류:', error);
      alert(`시즌 업데이트 실패: ${error.message}`);
    }
  };

  const handleSaveSeason = async () => {
    if (!editingSeason.name) {
      alert('시즌명을 입력해주세요.');
      return;
    }
    
    if (!productId || !supplyContractNo) {
      alert('상품번호와 공급계약번호가 필요합니다.');
      return;
    }

    try {
      // 0단계: 기존 시즌 리스트 조회하여 중복 체크
      const checkPayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) }
        ]
      };

      const checkUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
      const checkResponse = await fetch(checkUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(checkPayload)
      });

      if (checkResponse.ok) {
        const checkData = await checkResponse.json();
        if (checkData.table && checkData.table.length > 0 && checkData.table[0].정보) {
          const existingSeasons = JSON.parse(checkData.table[0].정보);
          const duplicate = existingSeasons.find(s => s.시즌명 === editingSeason.name.trim());
          
          if (duplicate) {
            alert(`"${editingSeason.name}" 시즌명이 이미 존재합니다.\n다른 이름을 사용해주세요.`);
            return;
          }
        }
      }

      // 1단계: 시즌 추가
      const addSeasonPayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) },
          { "컬럼명": "@시즌명", "값": String(editingSeason.name) },
          { "컬럼명": "@시즌색상", "값": String(editingSeason.color) },
          { "컬럼명": "@처리자", "값": String(userId) }
        ]
      };
const addUrl = `${API_URL}?procedureName=DBO.TRP_T_%EA%B0%80%EA%B2%A9%EB%93%B1%EB%A1%9D_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EC%B6%94%EA%B0%80`;
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(addSeasonPayload)
      });
if (!addResponse.ok) {
        const errorText = await addResponse.text();
        console.error('❌ 시즌 추가 실패 응답:', errorText);
        throw new Error(`시즌 추가 실패: ${addResponse.status} - ${errorText}`);
      }

      // 2단계: 시즌c 리스트 조회하여 시즌c번호 획듍
      const listPayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) }
        ]
      };

      const listUrl = `${API_URL}?procedureName=DBO.TRP_S_%EC%83%81%ED%92%88%EC%8B%9C%EC%A6%8C%EB%A6%AC%EC%8A%A4%ED%8A%B8`;
      const listResponse = await fetch(listUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(listPayload)
      });

      if (!listResponse.ok) throw new Error('시즌c 리스트 조회 실패');

      const listData = await listResponse.json();
      const seasonInfo = JSON.parse(listData.table[0].정보);
      
      // 방금 추가한 시즌 찾기 (시즌명으로 매칭 - 가장 마지막 항목)
      const newSeason = seasonInfo
        .filter(s => s.시즌명 === editingSeason.name)
        .pop(); // 가장 최근에 추가된 시즌 (마지막 항목)
if (!newSeason) {
        console.error('❌ 시즌을 찾을 수 없습니다. 전체 리스트:', seasonInfo);
        throw new Error('생성된 시즌을 찾을 수 없습니다.');
      }

      const seasonNo = newSeason.시즌번호;

      // 3단계: 시즌c 템플릿 저장 (날짜 지정)
      const dateList = selectedDates.map(date => ({ "날짜": date }));
      
      const templatePayload = {
        Table1: [
          { "컬럼명": "@업체번호", "값": String(companyNo) },
          { "컬럼명": "@상품번호", "값": String(productId) },
          { "컬럼명": "@날짜리스트", "값": JSON.stringify(dateList) },
          { "컬럼명": "@시즌번호", "값": Number(seasonNo) },
          { "컬럼명": "@공급계약번호", "값": Number(supplyContractNo) },
          { "컬럼명": "@처리자", "값": String(userId) }
        ]
      };

      const templateUrl = `${API_URL}?procedureName=DBO.TRP_T_%ED%98%B8%ED%85%94%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD_%EC%8B%9C%EC%A6%8C%ED%85%9C%ED%94%8C%EB%A6%BF%EC%A0%80%EC%9E%A5`;
      const templateResponse = await fetch(templateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(templatePayload)
      });

      if (!templateResponse.ok) throw new Error('시즌 템플릿 저장 실패');

      alert('시즌이 성공적으로 저장되었습니다.');
      setIsSeasonModalOpen(false);
      setEditingSeason(null);
      clearSelection();
      
      // 시즌 템플릿 재로드 (상태 트리거)
      setSeasonReloadTick(t => t + 1);
      setPriceReloadTick(t => t + 1);

      // 시즌 템플릿 저장 직후 자동 가격 저장 수행
      try {
        const createdTemplate = { no: Number(seasonNo), name: editingSeason.name, color: editingSeason.color };
        await autoSavePricingForSeason(createdTemplate);
      } catch (e) {
        console.warn('자동 가격 저장 실패(무시 가능):', e);
      }

      // 방금 생성한 시즌으로 가격 편집 모달 즉시 오픈 (필요 시)
      try {
        const createdTemplate = { no: Number(seasonNo), name: editingSeason.name, color: editingSeason.color };
        await handleViewSeasonPricing(createdTemplate);
      } catch (e) {
        console.warn('새 시즌 가격 편집 모달 오픈 실패(무시 가능):', e);
      }

    } catch (error) {
      console.error('시즌 저장 오류:', error);
      alert(`시즌 저장 실패: ${error.message}`);
    }
  };

  return (
    <div className="flex gap-4 h-full min-h-0">
      {/* 좌측 컨트롤 패널 */}
      <div className="w-80 flex-shrink-0 space-y-3 overflow-y-auto" data-no-clear="true">
      
        {/* 객실 타입 및 상품 선택 (내부 UI) */}
        {!hideInternalSelectors && roomTypeList.length > 0 && (
          <div className="bg-white border rounded-lg p-3">
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              🏨 객실 타입
            </label>
            <select
              value={effectiveSelectedRoomType || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (externalSelectedRoomType == null) setSelectedRoomType(value);
                if (typeof onSelectedRoomTypeChange === 'function') onSelectedRoomTypeChange(value);
                // 객실타입 변경 시 첫 번째 상품 자동 선택
                const roomType = roomTypeList.find(r => r.객실타입 === value);
                if (roomType && roomType.객실상품.length > 0) {
                  const firstProduct = roomType.객실상품[0].객실상품명;
                  if (externalSelectedRoomProduct == null) setSelectedRoomProduct(firstProduct);
                  if (typeof onSelectedRoomProductChange === 'function') onSelectedRoomProductChange(firstProduct);
                }
              }}
              className="w-full px-2 py-1.5 text-sm border rounded-md"
            >
              {roomTypeList.map((room) => (
                <option key={room.객실타입번호} value={room.객실타입}>{room.객실타입}</option>
              ))}
            </select>
            <label className="block text-xs font-semibold text-gray-700 mt-3 mb-2">
              💳 객실 상품 (요금제)
            </label>
            <select
              value={effectiveSelectedRoomProduct || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (externalSelectedRoomProduct == null) setSelectedRoomProduct(value);
                if (typeof onSelectedRoomProductChange === 'function') onSelectedRoomProductChange(value);
              }}
              className="w-full px-2 py-1.5 text-sm border rounded-md"
            >
              {roomTypeList
                .find(r => r.객실타입 === (effectiveSelectedRoomType || (roomTypeList[0] && roomTypeList[0].객실타입)))?.객실상품
                ?.map((p, idx) => (
                  <option key={idx} value={p.객실상품명}>{p.객실상품명}</option>
                ))}
            </select>
          </div>
        )}
      {/* 판매가 등록 모달 */}
      {isSalePriceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-no-clear="true">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">판매가 등록</h3>
              <button onClick={() => setIsSalePriceModalOpen(false)} className="p-2 hover:bg-gray-100 rounded" disabled={isSavingSalePrice}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">적용 대상</label>
                <div className="text-xs text-gray-600">선택된 날짜: <strong>{selectedDates.length}</strong>개</div>
                <div className="text-xs text-gray-600">객실 타입/상품: <strong>{effectiveSelectedRoomType || '-'}</strong> / <strong>{effectiveSelectedRoomProduct || '-'}</strong></div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">판매가 (성인)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full border rounded-lg px-3 py-2"
                  value={salePriceInput}
                  onChange={(e) => setSalePriceInput(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="숫자만 입력"
                />
                <p className="text-xs text-gray-500 mt-1">소아/유아는 0으로 저장됩니다.</p>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setIsSalePriceModalOpen(false)} className="px-4 py-2 border rounded-lg" disabled={isSavingSalePrice}>취소</button>
              <button
                onClick={async () => {
                  try {
                    if (!productId || !companyNo) { alert('상품/업체 정보가 없습니다.'); return; }
                    if (!selectedDates || selectedDates.length === 0) { alert('날짜를 선택하세요.'); return; }
                    const digits = String(salePriceInput || '').replace(/[^0-9]/g, '');
                    if (!digits) { alert('판매가를 입력하세요.'); return; }
                    // 선택한 날짜들의 가격번호 수집
                    const priceNos = selectedDates
                      .map(ds => priceStockData?.[ds]?.가격번호)
                      .filter(v => v != null && v !== '' && v !== 0);
                    if (priceNos.length === 0) {
                      alert('선택한 날짜에 수정 가능한 가격번호가 없습니다.');
                      return;
                    }
                    const listJson = JSON.stringify(priceNos.map(n => ({ 가격번호: String(n) })));
                    const payload = {
                      Table1: [
                        { 컬럼명: '@상품번호', 값: String(productId), 리스트값: null },
                        { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
                        { 컬럼명: '@처리자', 값: String(userId), 리스트값: null },
                        { 컬럼명: '@선택가격번호리스트', 값: listJson, 리스트값: null },
                        { 컬럼명: '@성인수정', 값: true, 리스트값: null },
                        { 컬럼명: '@소아수정', 값: true, 리스트값: null },
                        { 컬럼명: '@유아수정', 값: true, 리스트값: null },
                        { 컬럼명: '@성인판매가', 값: String(digits), 리스트값: null },
                        { 컬럼명: '@소아판매가', 값: 0, 리스트값: null },
                        { 컬럼명: '@유아판매가', 값: 0, 리스트값: null },
                      ],
                    };
                    setIsSavingSalePrice(true);
                    const url = `${API_URL}?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%ED%8C%90%EB%A7%A4%EA%B0%80`;
                    const res = await fetch(url, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                      body: JSON.stringify(payload),
                    });
                    if (!res.ok) {
                      const tx = await res.text();
                      throw new Error(`판매가 등록 실패: ${res.status} ${tx}`);
                    }
                    // 판매가 등록 성공 후, 선택된 날짜들의 시즌 지정 취소 수행
                    try {
                      if (companyNo && productId && supplyContractNo && selectedDates?.length > 0) {
                        const deleteDates = selectedDates.map(d => ({ "날짜": d }));
                        const deletePayload = {
                          Table1: [
                            { "컬럼명": "@업체번호", "값": String(companyNo) },
                            { "컬럼명": "@상품번호", "값": String(productId) },
                            { "컬럼명": "@삭제날짜", "값": JSON.stringify(deleteDates) },
                            { "컬럼명": "@공급계약번호", "값": Number(supplyContractNo) }
                          ]
                        };
                        const deleteUrl = `${API_URL}?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD_%EC%8B%9C%EC%A6%8C%ED%85%9C%ED%94%8C%EB%A6%BF%EC%82%AD%EC%A0%9C`;
                        const delRes = await fetch(deleteUrl, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                          body: JSON.stringify(deletePayload)
                        });
                        if (!delRes.ok) {
                          const dtx = await delRes.text();
                          console.error('시즌 지정 취소 실패:', delRes.status, dtx);
                        } else {
                          // 시즌 템플릿 새로고침
                          setSeasonReloadTick(t => t + 1);
                          // 자동 기본 시즌 재적용 방지 플래그 저장
                          try {
                            const supKey = `noDefaultSeason:${companyNo}:${productId}:${supplyContractNo}`;
                            const prev = JSON.parse(localStorage.getItem(supKey) || '[]');
                            const merged = Array.from(new Set([...(Array.isArray(prev) ? prev : []), ...selectedDates]));
                            localStorage.setItem(supKey, JSON.stringify(merged));
                          } catch {}
                        }
                      }
                    } catch (delErr) {
                      console.error('시즌 지정 취소 오류:', delErr);
                    }
                    alert('판매가가 등록되었습니다.');
                    setIsSalePriceModalOpen(false);
                    setSalePriceInput('');
                    // 데이터 갱신
                    setPriceReloadTick(t => t + 1);
                  } catch (e) {
                    console.error('판매가 등록 오류:', e);
                    alert(String(e?.message || e));
                  } finally {
                    setIsSavingSalePrice(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                disabled={isSavingSalePrice}
              >
                {isSavingSalePrice ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 텍스트 일괄 판매가 수정 모달 */}
      {isBulkSaleTextModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-no-clear="true">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">텍스트로 판매가 일괄 수정</h3>
              <button onClick={() => setIsBulkSaleTextModalOpen(false)} className="p-2 hover:bg-gray-100 rounded" disabled={isSavingBulkSale}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div className="text-xs text-gray-600">
                현재 선택된 객실 타입/상품 기준으로 아래 형식으로 붙여넣으면 해당 날짜의 판매가를 수정합니다.
              </div>
              <pre className="bg-gray-50 p-2 rounded text-[11px] text-gray-700">예)
2026-01-01 100000
2026-01-02 120000
2026-01-03 110000</pre>
              <textarea
                className="w-full h-48 border rounded-lg px-3 py-2 text-sm font-mono"
                placeholder="YYYY-MM-DD 가격"
                value={bulkSaleText}
                onChange={(e) => setBulkSaleText(e.target.value)}
              />
              <p className="text-[11px] text-gray-500">유효한 줄만 처리됩니다. 가격번호가 없는 날짜(미등록)는 건너뜁니다.</p>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setIsBulkSaleTextModalOpen(false)} className="px-4 py-2 border rounded-lg" disabled={isSavingBulkSale}>취소</button>
              <button
                onClick={async () => {
                  try {
                    if (!productId || !companyNo) { alert('상품/업체 정보가 없습니다.'); return; }
                    if (!bulkSaleText.trim()) { alert('입력값이 없습니다.'); return; }
                    // 파싱: 라인별로 YYYY-MM-DD, price 추출
                    const lines = bulkSaleText.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                    const datePricePairs = [];
                    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})\s+(\d[\d,]*)$/;
                    for (const line of lines) {
                      const m = line.match(dateRegex);
                      if (!m) continue;
                      const d = `${m[1]}-${m[2]}-${m[3]}`;
                      const price = m[4].replace(/[^0-9]/g, '');
                      if (!price) continue;
                      datePricePairs.push({ date: d, price });
                    }
                    if (datePricePairs.length === 0) { alert('유효한 입력 줄이 없습니다.'); return; }

                    // 날짜 -> 가격번호 매핑
                    const notFoundDates = [];
                    const grouped = new Map(); // price -> { priceNos: [], dates: [] }
                    for (const { date, price } of datePricePairs) {
                      const pno = priceStockData?.[date]?.가격번호;
                      if (pno == null || pno === '' || Number(pno) === 0) {
                        notFoundDates.push(date);
                        continue;
                      }
                      if (!grouped.has(price)) grouped.set(price, { priceNos: [], dates: [] });
                      const bucket = grouped.get(price);
                      bucket.priceNos.push(String(pno));
                      bucket.dates.push(date);
                    }
                    if (grouped.size === 0) { alert('수정 가능한 가격번호가 없습니다.'); return; }

                    setIsSavingBulkSale(true);
                    const updatedDates = [];
                    for (const [price, bucket] of grouped.entries()) {
                      const listJson = JSON.stringify(bucket.priceNos.map(n => ({ 가격번호: String(n) })));
                      const payload = {
                        Table1: [
                          { 컬럼명: '@상품번호', 값: String(productId), 리스트값: null },
                          { 컬럼명: '@공급업체번호', 값: String(companyNo), 리스트값: null },
                          { 컬럼명: '@처리자', 값: String(userId), 리스트값: null },
                          { 컬럼명: '@선택가격번호리스트', 값: listJson, 리스트값: null },
                          { 컬럼명: '@성인수정', 값: true, 리스트값: null },
                          { 컬럼명: '@소아수정', 값: true, 리스트값: null },
                          { 컬럼명: '@유아수정', 값: true, 리스트값: null },
                          { 컬럼명: '@성인판매가', 값: String(price), 리스트값: null },
                          { 컬럼명: '@소아판매가', 값: 0, 리스트값: null },
                          { 컬럼명: '@유아판매가', 값: 0, 리스트값: null },
                        ],
                      };
                      const url = `${API_URL}?procedureName=DBO.RCT_T_%EC%83%81%ED%92%88%EA%B0%80%EA%B2%A9_%ED%8C%90%EB%A7%A4%EA%B0%80`;
                      const res = await fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                        body: JSON.stringify(payload),
                      });
                      if (!res.ok) {
                        const tx = await res.text();
                        throw new Error(`판매가 등록 실패: ${res.status} ${tx}`);
                      }
                      updatedDates.push(...bucket.dates);
                    }

                    // 시즌 지정 취소: 업데이트된 날짜 전체에 대해 수행
                    try {
                      if (companyNo && productId && supplyContractNo && updatedDates.length > 0) {
                        const deleteDates = Array.from(new Set(updatedDates)).map(d => ({ 날짜: d }));
                        const deletePayload = {
                          Table1: [
                            { 컬럼명: '@업체번호', 값: String(companyNo) },
                            { 컬럼명: '@상품번호', 값: String(productId) },
                            { 컬럼명: '@삭제날짜', 값: JSON.stringify(deleteDates) },
                            { 컬럼명: '@공급계약번호', 값: Number(supplyContractNo) }
                          ]
                        };
                        const deleteUrl = `${API_URL}?procedureName=DBO.TRP_S_%ED%98%B8%ED%85%94_%EA%B3%B5%EA%B8%89%EA%B3%84%EC%95%BD_%EC%8B%9C%EC%A6%8C%ED%85%9C%ED%94%8C%EB%A6%BF%EC%82%AD%EC%A0%9C`;
                        const delRes = await fetch(deleteUrl, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                          body: JSON.stringify(deletePayload)
                        });
                        if (!delRes.ok) {
                          const dtx = await delRes.text();
                          console.error('시즌 지정 취소 실패:', delRes.status, dtx);
                        } else {
                          setSeasonReloadTick(t => t + 1);
                          try {
                            const supKey = `noDefaultSeason:${companyNo}:${productId}:${supplyContractNo}`;
                            const prev = JSON.parse(localStorage.getItem(supKey) || '[]');
                            const merged = Array.from(new Set([...(Array.isArray(prev) ? prev : []), ...updatedDates]));
                            localStorage.setItem(supKey, JSON.stringify(merged));
                          } catch {}
                        }
                      }
                    } catch (delErr) {
                      console.error('시즌 지정 취소 오류:', delErr);
                    }

                    let msg = `완료: ${updatedDates.length}개 날짜 업데이트`;
                    if (notFoundDates.length > 0) msg += `\n건너뜀(가격번호 없음): ${notFoundDates.length}개`;
                    alert(msg);
                    setIsBulkSaleTextModalOpen(false);
                    setBulkSaleText('');
                    setPriceReloadTick(t => t + 1);
                  } catch (e) {
                    console.error('텍스트 일괄 판매가 수정 오류:', e);
                    alert(String(e?.message || e));
                  } finally {
                    setIsSavingBulkSale(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                disabled={isSavingBulkSale}
              >
                {isSavingBulkSale ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
        {/* 날짜 범위 선택 + 요일 필터 (조건부 노출) */}
        <div className="bg-white border rounded-lg p-3">
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            📅 시즌 지정날짜  선택
          </label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={rangeStart}
              onChange={(e) => handleRangeStartChange(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
              placeholder="시작일"
            />
            <span className="text-gray-400 text-xs">~</span>
            <input
              type="date"
              value={rangeEnd}
              onChange={(e) => handleRangeEndChange(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
              placeholder="종료일"
            />
          </div>

          {rangeStart && rangeEnd && rangeStart <= rangeEnd && (
            <div className="mt-3 pt-3 border-t">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700">적용 요일</label>
                {!holidayOnly && (
                  <button
                    onClick={toggleAllWeekdays}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {weekdayFilters.length === 7 ? '전체 해제' : '전체 선택'}
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex gap-0.5">
                  {weekdayNames.map((day, idx) => {
                    const dayValue = weekdayValues[idx];
                    return (
                      <label
                        key={idx}
                        className="flex items-center gap-0.5 px-1.5 py-1 border rounded cursor-pointer hover:bg-gray-50 transition-colors flex-1 justify-center"
                      >
                        <input
                          type="checkbox"
                          checked={weekdayFilters.includes(dayValue)}
                          onChange={() => toggleWeekdayFilter(dayValue)}
                          className="rounded w-3 h-3"
                          disabled={holidayOnly}
                        />
                        <span className={`text-xs font-medium ${
                          dayValue === 0 ? 'text-red-600' : dayValue === 6 ? 'text-blue-600' : 'text-gray-700'
                        } ${holidayOnly ? 'opacity-50' : ''}`}>
                          {day}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <label className="flex items-center gap-1 px-2 py-1 border rounded cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={holidayOnly}
                    onChange={toggleHolidayFilter}
                    className="rounded w-3 h-3"
                  />
                  <span className="text-xs font-medium text-red-600">🎊 공휴일만</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* 시즌 템플릿 범례 */}
        {Object.keys(seasonTemplates).length > 0 && (
          <div className="bg-gradient-to-r from-gray-50 to-black-50 border-2 border-grya-100 rounded-lg p-3" data-no-clear="true">
            {/* 선택 정보 & 액션 */}
            {selectedDates.length > 0 && (
              <div className="mb-3 pb-3 border-b border-blue-200" ref={selectionActionsRef}>
                <div className="text-center mb-2">
                  <span className="text-2xl font-bold text-blue-600">{selectedDates.length}</span>
                  <span className="text-xs text-gray-600 ml-1">개 날짜 선택됨</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsStockModalOpen(true)}
                    className="flex-1 px-3 py-2.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-800 font-semibold shadow-sm transition-all hover:shadow-md flex items-center justify-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    재고/마감 관리
                  </button>
                  <button
                    onClick={() => setIsSalePriceModalOpen(true)}
                    className="flex-1 px-3 py-2.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-sm transition-all hover:shadow-md flex items-center justify-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    판매가 수정
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-1.5 mb-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xs font-semibold text-blue-900">적용된 시즌</h3>
              <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                {Object.keys(seasonTemplates).length}
              </span>
              {selectedDates.length > 0 && (
                <span className="text-xs text-gray-600 ml-auto">클릭하여 적용</span>
              )}
              <button
                onClick={() => { setIsSeasonEditMode(v => !v); setEditingInlineSeason(null); }}
                className={`text-xs px-2 py-1 rounded border ml-auto ${isSeasonEditMode ? 'bg-yellow-100 border-yellow-300 text-yellow-800' : 'text-gray-700 hover:bg-gray-50'}`}
                title="시즌 이름/색상 편집 토글"
              >
                {isSeasonEditMode ? '편집 종료' : '시즌 편집'}
              </button>
            </div>
            <div className="space-y-1.5">
              {Array.from(new Set(Object.values(seasonTemplates).map(t => JSON.stringify(t))))
                .map(str => JSON.parse(str))
                .sort((a, b) => {
                  const aIsDefault = a.name === DEFAULT_SEASON_NAME;
                  const bIsDefault = b.name === DEFAULT_SEASON_NAME;
                  if (aIsDefault && !bIsDefault) return -1;
                  if (!aIsDefault && bIsDefault) return 1;
                  return 0;
                })
                .map((template, idx) => {
                  const count = Object.values(seasonTemplates).filter(t => t.name === template.name).length;
                  const isEditingThis = !!editingInlineSeason && (
                    (editingInlineSeason.no != null && template.no === editingInlineSeason.no) ||
                    (editingInlineSeason.no == null && template.name === editingInlineSeason.name)
                  );
                  return (
                    <div
                      key={idx}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 bg-white border rounded shadow-sm text-left ${!isSeasonEditMode ? 'cursor-pointer hover:bg-blue-50 hover:border-blue-300' : ''}`}
                      style={{ borderLeftWidth: '3px', borderLeftColor: template.color }}
                      title={selectedDates.length > 0 ? '시즌 업데이트' : '가격 정보 보기'}
                      onClick={
                        !isSeasonEditMode
                          ? () => {
                              if (selectedDates.length > 0) {
                                handleUpdateSeasonTemplate(template);
                              } else {
                                handleViewSeasonPricing(template);
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: template.color }} />
                      {!isEditingThis ? (
                        <>
                          <span className="text-xs font-medium flex-1">{template.name}</span>
                          <span className="text-xs text-gray-500">({count}일)</span>
                          {isSeasonEditMode && template.name !== DEFAULT_SEASON_NAME && (
                            <button
                              className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                              onClick={(e) => { e.stopPropagation(); startInlineSeasonEdit(template); }}
                            >
                              편집
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-2 flex-1 flex-wrap">
                          <input
                            type="text"
                            value={editingInlineSeason.name}
                            onChange={(e) => setEditingInlineSeason(prev => ({ ...prev, name: e.target.value }))}
                            className="flex-1 min-w-0 border rounded px-2 py-1 text-xs"
                            placeholder="시즌명"
                          />
                          <input
                            type="color"
                            value={editingInlineSeason.color}
                            onChange={(e) => setEditingInlineSeason(prev => ({ ...prev, color: e.target.value }))}
                            className="w-8 h-6 border rounded shrink-0"
                            title="시즌 색상"
                          />
                          <button
                            className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700 shrink-0"
                            onClick={(e) => { e.stopPropagation(); saveInlineSeasonEdit(); }}
                          >
                            저장
                          </button>
                          <button
                            className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 shrink-0"
                            onClick={(e) => { e.stopPropagation(); cancelInlineSeasonEdit(); }}
                          >
                            취소
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            
            {/* 새 시즌 생성 버튼 */}
            {selectedDates.length > 0 && (
              <button
                onClick={handleCreateSeason}
                data-no-clear="true"
                className="w-full mt-2 px-3 py-2 text-xs bg-white border-0.5 borderd border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-green-500 font-semibold transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                선택한 날짜로 새 시즌 생성
              </button>
            )}
          </div>
        )}

        {/* 시즌 템플릿이 아직 없을 때: 생성 안내 + 액션 */}
        {Object.keys(seasonTemplates).length === 0 && (
          <div className="bg-white border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <h3 className="text-xs font-semibold text-gray-800">시즌이 없습니다</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">날짜를 선택한 뒤 아래 버튼으로 첫 시즌을 생성하세요.</p>
            <button
              onClick={handleCreateSeason}
              data-no-clear="true"
              className="w-full px-3 py-2.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all hover:shadow-md flex items-center justify-center gap-2"
              disabled={selectedDates.length === 0}
              title={selectedDates.length === 0 ? '먼저 날짜를 선택하세요' : '선택한 날짜로 새 시즌 생성'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              새 시즌 생성
            </button>
            <div className="text-[11px] text-gray-500 mt-2 text-center">선택된 날짜: <strong>{selectedDates.length}</strong>개</div>
          </div>
        )}
      </div>

      {/* 우측 캘린더 영역 */}
      <div className="flex-1 flex flex-col gap-3 overflow-y-auto" ref={calendarAreaRef}>
        {/* 캘린더 표시 기간 & 선택 초기화 */}
        <div className="bg-white border rounded-lg p-3">
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-700 whitespace-nowrap">
              🗓️ 캘린더 표시 기간
            </label>
            <input
              type="month"
              value={calendarStartMonth}
              onChange={(e) => setCalendarStartMonth(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
            />
            <span className="text-gray-400 text-xs">~</span>
            <input
              type="month"
              value={calendarEndMonth}
              onChange={(e) => setCalendarEndMonth(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
            />
            {/* 텍스트 일괄 수정 버튼 */}
            <button
              onClick={() => setIsBulkSaleTextModalOpen(true)}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
              title="YYYY-MM-DD 가격 형식으로 붙여넣어 판매가를 일괄 수정"
              disabled={!effectiveSelectedRoomType || !effectiveSelectedRoomProduct}
            >
              판매가 일괄 등록
            </button>

            {selectedDates.length > 0 && (
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 text-xs bg-gray-100 border border-gray-300 text-gray-700 rounded hover:bg-gray-200 font-medium whitespace-nowrap transition-colors flex items-center gap-1.5"
                title="선택한 날짜 초기화"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                선택한 날짜 초기화 ({selectedDates.length})
              </button>
            )}
          </div>
        </div>

        {/* 캘린더 그리드 */}
        <div className="bg-white border rounded-lg p-4 flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {calendarMonths.map(({ year, month, dates }, monthIdx) => (
          <div key={monthIdx} className="bg-white border rounded-lg p-4">
            <h3 className="text-center font-semibold mb-3">{year}년 {month + 1}월</h3>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekdayNames.map((day, idx) => {
                const dayValue = weekdayValues[idx];
                return (
                  <div key={day} className={`text-center text-xs font-semibold py-1 ${
                    dayValue === 0 ? 'text-red-600' : dayValue === 6 ? 'text-blue-600' : 'text-gray-700'
                  }`}>
                    {day}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {dates.map(({ date, isCurrentMonth }, idx) => {
                const isSelected = isDateSelected(date);
                const season = getSeasonForDate(date);
                const isHoliday = holidays2025.includes(formatDate(date));
                const isPastDate = date < startOfToday;
                
                // 현재 월이 아닌 날짜는 빈 칸으로 표시
                if (!isCurrentMonth) {
                  return <div key={idx} className="aspect-square" />;
                }
                
                // 날짜별 가격/재고 정보
                const dateStr = formatDate(date);
                const priceInfo = priceStockData[dateStr];
                
                return (
                  <button
                    key={idx}
                    onMouseDown={() => handleDragStart(date)}
                    onMouseEnter={() => handleDragOver(date)}
                    onMouseUp={handleDragEnd}
                    className={`p-1 text-xs rounded-lg relative cursor-pointer hover:bg-gray-100 border transition-colors ${
                      isSelected ? 'border-2 border-blue-600' : 'border-gray-200 hover:border-gray-300'
                    } ${priceInfo?.마감여부 || isPastDate ? 'opacity-50' : ''}`}
                    style={{
                      minHeight: '60px'
                    }}
                    title={season ? `${season.name}` : ''}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <div className="font-semibold mb-1">
                        {season ? (
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px]"
                            style={{ backgroundColor: season.color, color: isWhiteColor(season.color) ? '#000000' : '#ffffff' }}
                          >
                            {date.getDate()}
                          </span>
                        ) : (
                          <span className={`text-[12px] ${isHoliday ? 'text-red-600' : 'text-gray-800'}`}>{date.getDate()}</span>
                        )}
                      </div>
                      {priceInfo && (
                        <>
                          <div className="text-[10px] font-bold text-blue-600">
                            {priceInfo.판매가 ? formatKRW(priceInfo.판매가) : ''}
                          </div>
                          <div className="text-[9px] text-gray-600">
                            재고: {priceInfo.보유석 ?? 0}/판매{priceInfo.판매석 ?? 0}
                          </div>
                          {(priceInfo.최소투숙일 != null && Number(priceInfo.최소투숙일) > 1) && (
                            <div className="text-[9px] text-emerald-700">
                              최소투숙일: {priceInfo.최소투숙일}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {priceInfo?.마감여부 && (
                      <span className="absolute top-0 left-0 m-0.5 px-0.5 py-0.5 text-[9px] font-bold rounded text-red-700">마감</span>
                    )}
                    {isHoliday && !season && (
                      <span className="absolute top-0 right-0 w-1.5 h-1.5 bg-red-500 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
          </div>
        </div>
      </div>

      {/* 시즌 편집 모달 (기존 시즌의 이름/색상만 편집) */}
      {isEditSeasonModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-no-clear="true">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">시즌 편집</h3>
              <button onClick={() => setIsEditSeasonModalOpen(false)} className="p-2 hover:bg-gray-100 rounded">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">시즌명 *</label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2"
                  value={editingExistingSeason.name}
                  onChange={(e) => setEditingExistingSeason(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">색상</label>
                <div className="flex gap-2 flex-wrap">
                  {colorPalette.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setEditingExistingSeason(prev => ({ ...prev, color: c.value }))}
                      className={`w-8 h-8 rounded-full ${editingExistingSeason.color === c.value ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
                      style={{ backgroundColor: c.value }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setIsEditSeasonModalOpen(false)}
                className="px-4 py-2 border rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleSaveEditedSeason}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 시즌 모달 */}
      {isSeasonModalOpen && editingSeason && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-no-clear="true">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">시즌 생성</h3>
              <button onClick={() => setIsSeasonModalOpen(false)} className="p-2 hover:bg-gray-100 rounded">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">시즌명 *</label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2"
                  value={editingSeason.name}
                  onChange={(e) => setEditingSeason({ ...editingSeason, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">색상</label>
                <div className="flex gap-2">
                  {colorPalette.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setEditingSeason({ ...editingSeason, color: color.value })}
                      className={`w-8 h-8 rounded-full ${
                        editingSeason.color === color.value ? 'ring-2 ring-offset-2' : ''
                      }`}
                      style={{ backgroundColor: color.value }}
                    />
                  ))}
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-sm">선택된 날짜: <strong>{selectedDates.length}</strong>개</p>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setIsSeasonModalOpen(false)}
                className="px-4 py-2 border rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleSaveSeason}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 시즌 가격 정보 모달 */}
      {isPricingModalOpen && selectedSeasonPricing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-no-clear="true">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="border-b px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: selectedSeasonPricing.template.color }}
                />
                <h3 className="text-lg font-bold">
                  {selectedSeasonPricing.template.name} 시즌 가격 정보
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setIsPricingModalOpen(false)} 
                  className="p-2 hover:bg-gray-100 rounded"
                  disabled={isSavingPrice}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* 전체 객실 가격 정보 */}
              <div>
                <h4 className="text-sm text-gray-600 mb-3 mb-3">전체 객실 가격 정보</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">객실 타입</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">객실 상품</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">가격</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getPricingRows().map((row, idx) => (
                        <tr key={row.key || idx} className={
                          row.room.객실명 === effectiveSelectedRoomType && row.product.상품명 === effectiveSelectedRoomProduct ? 'bg-blue-50' : ''
                        }>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">{(row.room.객실번호 && resourceOptionMap.roomNameById[row.room.객실번호]) || row.room.객실명}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">{(row.product.상품번호 && resourceOptionMap.productNameById[row.product.상품번호]) || row.product.상품명}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            {row.type === 'existing' ? (
                              isEditingPrice ? (
                                <input
                                  type="text"
                                  className="w-full border rounded px-2 py-1 text-sm font-medium text-red-600"
                                  value={formatKRW(row.product.값 || '')}
                                  onChange={(e) => {
                                    const seasonIndex = editedPricingData.시즌.findIndex(s => (
                                      selectedSeasonPricing?.template?.no != null
                                        ? s.시즌번호 === selectedSeasonPricing.template.no
                                        : s.시즌명 === selectedSeasonPricing.template.name
                                    ));
                                    handleEditPrice(seasonIndex, row.rIdx, row.pIdx, onlyDigits(e.target.value));
                                  }}
                                />
                              ) : (
                                <span className="font-medium text-red-600">{formatKRW(row.product.값 || '0')}</span>
                              )
                            ) : (
                              isEditingPrice ? (
                                <input
                                  type="text"
                                  className="w-full border rounded px-2 py-1 text-sm font-medium text-red-600"
                                  value={formatKRW(getEditedValueFor(row.room.객실번호, row.room.객실명, row.product.상품번호, row.product.상품명) || '')}
                                  onChange={(e) => handleEditMissingPrice(
                                    row.room.객실번호,
                                    row.room.객실명,
                                    row.product.상품번호,
                                    row.product.상품명,
                                    onlyDigits(e.target.value)
                                  )}
                                  placeholder="미등록"
                                />
                              ) : (
                                (() => {
                                  const val = getEditedValueFor(row.room.객실번호, row.room.객실명, row.product.상품번호, row.product.상품명);
                                  return val ? (
                                    <span className="font-medium text-red-600">{formatKRW(val)}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  );
                                })()
                              )
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end sticky bottom-0 bg-white gap-2">
              <button
                onClick={() => {
                  if (isEditingPrice) {
                    handleSavePricing();
                  } else {
                    setIsEditingPrice(true);
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                disabled={isSavingPrice}
              >
                {isSavingPrice ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    저장 중...
                  </>
                ) : (
                  isEditingPrice ? '저장' : '편집'
                )}
              </button>
              {isEditingPrice && (
                <button
                  onClick={() => setIsEditingPrice(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  disabled={isSavingPrice}
                >
                  취소
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 재고 등록 모달 */}
      <StockRegistrationModal
        isOpen={isStockModalOpen}
        onClose={() => setIsStockModalOpen(false)}
        selectedDates={selectedDates}
        productId={productId}
        companyNo={companyNo}
        userId={userId}
        roomTypeNo={roomTypeList.find(r => r.객실타입 === effectiveSelectedRoomType)?.객실타입번호}
        roomTypeName={effectiveSelectedRoomType}
        roomProductName={effectiveSelectedRoomProduct}
        initialStockCount={commonPrefill.stock}
        initialCutoffEnabled={commonPrefill.cutoffEnabled}
        initialCutoffMode={commonPrefill.cutoffMode}
        initialMinStayEnabled={commonPrefill.minStayEnabled}
        initialMinStayDays={commonPrefill.minStayDays}
        onSuccess={() => {
          // 재고 등록 성공 시 가격/재고 데이터 새로고침
          setPriceReloadTick(t => t + 1);
        }}
      />
    </div>
  );
};

export default UnifiedPriceCalendar;
